import { useEffect, useRef, useState, useCallback } from 'react'
import {
  Renderer, Stave, StaveNote, Voice, Formatter,
  Accidental, StaveConnector, Barline, Stem, Beam,
} from 'vexflow'
import { groupIntoMeasures, pitchToVex, isInRange, BEAT_VALUES, KEY_SCALE_NOTES, restEntriesForBeats, totalBeats } from '../utils/musicTheory'
import { isChordSymbolIB64Figure } from '../utils/chordSymbols'
import { parseChordSymbolParts, splitFiguredStackFlatDigitBottom } from '../utils/chordSymbolFigureLayout'

const TREBLE_Y = 20
const BASS_Y = 148
/** rowY 기준: 위·아래 스탭(그랜드스태프) 구분선 — 트레블 아래·베이스 위 사이 */
const GRAND_STAFF_SPLIT_Y = 124
const CHORD_ROMAN_Y = BASS_Y + 128 + 23

/** 첫 마디(조표·박자) / 이후 마디 — 분석 전 기본 너비 (분석 결과 있으면 minStaveWidthForChordAnalysis가 필요한 만큼만 더 늘림) */
const FIRST_W_BASE = 220
const REST_W_BASE = 160
const X0 = 16

/** 음표/쉼표 1개당 대략 필요 폭(px) — 8분·16분이 많으면 마디를 넓혀 마디선 넘침 방지 */
const TICKABLE_W = {
  w: 40, h: 30, q: 24, '8': 28, '16': 26,
  wr: 32, hr: 24, qr: 18, '8r': 16, '16r': 14,
}

/** 드래그 1스텝당 픽셀 (온음계 1도) */
const DRAG_STEP_PX = 5

/** harmony_engine.tokens_at_beat와 동일한 시점: t = min(beatIdx+0.5, bpp-eps) */
const BEAT_SAMPLE_EPS = 1e-5

const CHROMATIC_SHARP = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B']
const CHROMATIC_FLAT  = ['C','Db','D','Eb','E','F','Gb','G','Ab','A','Bb','B']
const LETTER_PC = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }
const ACC_SEMITONES = { '': 0, '#': 1, '##': 2, b: -1, bb: -2 }

function noteToPC(note) {
  const nm = String(note).match(/^([A-G])(##|#|bb|b)?$/)
  if (nm) return (((LETTER_PC[nm[1]] + (ACC_SEMITONES[nm[2] ?? ''] ?? 0)) % 12) + 12) % 12
  const i = CHROMATIC_SHARP.indexOf(note)
  return i !== -1 ? i : CHROMATIC_FLAT.indexOf(note)
}

function localPitchToMidi(pitch) {
  const m = String(pitch).match(/^([A-G])(##|#|bb|b|n)?(\d+)$/)
  if (!m) return null
  const noteName = m[2] === 'n' ? m[1] : (m[1] + (m[2] ?? ''))
  const pc = noteToPC(noteName)
  if (pc < 0) return null
  return (parseInt(m[3], 10) + 1) * 12 + pc
}

/**
 * pitch를 조성의 온음계 스텝으로 이동시킨다.
 * 조성에 없는 임시표 음표는 가장 가까운 음계음으로 스냅 후 이동.
 * MIDI 방향 검증으로 비-C 키에서 옥타브 점프 방지.
 */
function shiftPitchDiatonic(pitch, steps, key) {
  if (steps === 0) return pitch
  const m = String(pitch).match(/^([A-G])(##|#|bb|b|n)?(\d+)$/)
  if (!m) return pitch
  const note = m[2] === 'n' ? m[1] : (m[1] + (m[2] ?? ''))
  const octave = parseInt(m[3], 10)
  const scale = KEY_SCALE_NOTES[key] ?? KEY_SCALE_NOTES['C']

  let idx = scale.indexOf(note)
  if (idx === -1) {
    // 임시표 음표: PC 기준 가장 가까운 음계음으로 스냅
    const pc = noteToPC(note)
    let bestIdx = 0, bestDist = 12
    for (let i = 0; i < 7; i++) {
      const d = Math.min(Math.abs(noteToPC(scale[i]) - pc), 12 - Math.abs(noteToPC(scale[i]) - pc))
      if (d < bestDist) { bestDist = d; bestIdx = i }
    }
    idx = bestIdx
  }

  const origMidi = localPitchToMidi(`${scale[idx]}${octave}`)

  let newIdx = idx + steps
  let newOctave = octave
  while (newIdx >= 7) { newIdx -= 7; newOctave++ }
  while (newIdx < 0)  { newIdx += 7; newOctave-- }

  // MIDI 방향 검증: 비-C 키에서 옥타브가 잘못 계산될 수 있으므로 보정
  const newMidi = localPitchToMidi(`${scale[newIdx]}${newOctave}`)
  if (origMidi != null && newMidi != null) {
    if (steps > 0 && newMidi <= origMidi) newOctave++
    if (steps < 0 && newMidi >= origMidi) newOctave--
  }

  return `${scale[newIdx]}${newOctave}`
}

/** 선택/히트맵용: flatIndex 기준으로 선택 하이라이트 적용 */
function applySelectionStyle(vexArr, notes, cumFlat, selNote, voice) {
  if (selNote?.voice !== voice) return
  for (let j = 0; j < notes.length; j++) {
    if (cumFlat + j === selNote.flatIndex) {
      try { vexArr[j]?.setStyle({ fillStyle: '#1d4ed8', strokeStyle: '#1d4ed8' }) } catch {}
      break
    }
  }
}

/**
 * 렌더 이후 각 음표·쉼표의 화면 좌표를 hitmap에 추가.
 * 반환값: { nonRestCount, totalCount }
 */
/**
 * 실제 데이터가 없는(마디를 다 안 채운) 나머지 구간에도 fillMeasureToBeats가 그리는
 * 자리표시자 쉼표와 1:1 대응하는 "가상 항목"을 붙여서 collectHitmap이 그 쉼표도
 * 클릭 가능하게 만든다 — 클릭하면 그 자리까지 실제 쉼표를 채워 넣고 선택한다.
 */
function withFillerEntries(trunc, beatsPerMeasure) {
  const filler = restEntriesForBeats(beatsPerMeasure - totalBeats(trunc))
  const withPad = filler.map((e, idx) => ({ ...e, _filler: true, _fillerPad: filler.slice(0, idx + 1) }))
  return [...trunc, ...withPad]
}

function collectHitmap(vexArr, notes, cumNonRest, cumFlat, voice, hitmap) {
  let nonRestCount = 0
  for (let j = 0; j < notes.length && j < vexArr.length; j++) {
    try {
      const vn = vexArr[j]
      const x = vn.getAbsoluteX()
      const ys = vn.getYs()
      const y = ys?.[0] ?? null
      if (y === null) { if (!notes[j].isRest) nonRestCount++; continue }
      if (notes[j].isRest) {
        hitmap.push({
          x, y, voice, flatIndex: cumFlat + j, isRest: true, duration: notes[j].duration,
          isFiller: !!notes[j]._filler, padEntries: notes[j]._fillerPad,
        })
      } else {
        hitmap.push({ x, y, voice, flatIndex: cumFlat + j, noteIndex: cumNonRest + nonRestCount, pitch: notes[j].pitch, isRest: false })
        nonRestCount++
      }
    } catch { /* ignore */ }
  }
  return { nonRestCount, totalCount: notes.length }
}

function makeVexNote(pitch, duration, stemDir, clef, voice, theoryBase = 'baek') {
  const vexKey = pitchToVex(pitch)
  const note = new StaveNote({
    keys: [vexKey],
    duration,
    stem_direction: stemDir,
    clef,
  })
  if (duration === 'h') {
    const baseGetStemX = StaveNote.prototype.getStemX
    note.getStemX = function getStemXHalfNote() {
      const x = baseGetStemX.call(this)
      const dir = this.getStemDirection()
      const nudgeX = 1.2
      return x + (dir === Stem.UP ? -nudgeX : nudgeX)
    }
  }
  const accMatch = pitch.match(/^[A-G](##|#|bb|b|n)/)
  if (accMatch?.[1]) note.addModifier(new Accidental(accMatch[1]), 0)

  if (!isInRange(pitch, voice, theoryBase)) {
    note.setStyle({ fillStyle: '#dc2626', strokeStyle: '#dc2626' })
  }
  return note
}

/**
 * 한 오선에 두 성부가 같이 있을 때 쉼표 세로 위치.
 * VexFlow는 keys만으로는 온쉼표 줄을 강제 고정하므로 setKeyLine으로 덮어쓴다.
 * 또한 같은 길이 쉼표는 아래 성부를 draw=false로 숨기므로, 포맷 후 다시 켠다.
 */
const REST_LINE_BY_VOICE = {
  soprano: 6, // 고음자리표 위쪽
  alto: 1,    // 고음자리표 아래쪽
  tenor: 6,   // 저음자리표 위쪽
  bass: 1,    // 저음자리표 아래쪽
}

function makeRestNote(duration, clef, voice) {
  const line = REST_LINE_BY_VOICE[voice] ?? 3
  const restKey = clef === 'bass'
    ? (voice === 'tenor' ? 'f/3' : 'b/2')
    : (voice === 'soprano' ? 'd/5' : 'g/4')
  const note = new StaveNote({ keys: [restKey], duration: duration + 'r', clef })
  try {
    note.setKeyLine(0, line)
  } catch { /* ignore */ }
  return note
}

/**
 * Formatter/ModifierContext가 동일 길이 쉼표를 하나로 합친 뒤에도
 * S·A·T·B 쉼표가 각각 보이게 줄을 다시 갈라 놓고 draw를 강제한다.
 */
function ensureSatbRestsVisible(sv, av, tv, bv) {
  const apply = (arr, voice) => {
    const line = REST_LINE_BY_VOICE[voice] ?? 3
    for (const vn of arr) {
      if (!vn || typeof vn.isRest !== 'function' || !vn.isRest()) continue
      try {
        if (vn.render_options) vn.render_options.draw = true
        vn.setKeyLine(0, line)
      } catch { /* ignore */ }
    }
  }
  apply(sv, 'soprano')
  apply(av, 'alto')
  apply(tv, 'tenor')
  apply(bv, 'bass')
}

function vexNoteBeats(vn) {
  let durStr = 'q'
  let dots = 0
  try { durStr = (vn.getDuration?.() ?? 'q').replace('r', '') } catch { /* ignore */ }
  try { dots = vn.getDots?.() ?? 0 } catch { /* ignore */ }
  let beats = BEAT_VALUES[durStr] ?? 1
  for (let d = 0; d < dots; d++) beats *= 1.5
  return beats
}

function truncateNotesForMeasure(notes, beatsPerMeasure) {
  const truncated = []
  let sum = 0
  for (const n of notes) {
    const nb = BEAT_VALUES[n.duration] ?? 1
    if (sum + nb <= beatsPerMeasure + 0.0001) {
      truncated.push(n)
      sum += nb
    } else break
  }
  return truncated
}

function fillMeasureToBeats(notes, beatsPerMeasure, clef, stemDir, voice, theoryBase = 'baek') {
  const truncated = truncateNotesForMeasure(notes, beatsPerMeasure)

  const vexNotes = []
  let beats = 0
  for (const n of truncated) {
    if (n.isRest) {
      const restDuration = n.duration.replace('r', '')
      vexNotes.push(makeRestNote(restDuration, clef, voice))
    } else {
      vexNotes.push(makeVexNote(n.pitch, n.duration, stemDir, clef, voice, theoryBase))
    }
    beats += BEAT_VALUES[n.duration] ?? 1
  }

  let remaining = beatsPerMeasure - beats
  while (remaining > 0.001) {
    if (remaining >= 3.999) {
      vexNotes.push(makeRestNote('w', clef, voice)); remaining -= 4
    } else if (remaining >= 1.999) {
      vexNotes.push(makeRestNote('h', clef, voice)); remaining -= 2
    } else if (remaining >= 0.999) {
      vexNotes.push(makeRestNote('q', clef, voice)); remaining -= 1
    } else if (remaining >= 0.499) {
      vexNotes.push(makeRestNote('8', clef, voice)); remaining -= 0.5
    } else {
      vexNotes.push(makeRestNote('q', clef, voice)); remaining -= 1
    }
  }

  if (vexNotes.length === 0) {
    if (beatsPerMeasure >= 4) {
      vexNotes.push(makeRestNote('w', clef, voice))
    } else if (beatsPerMeasure >= 2) {
      vexNotes.push(makeRestNote('h', clef, voice))
      if (beatsPerMeasure >= 3) vexNotes.push(makeRestNote('q', clef, voice))
    } else {
      vexNotes.push(makeRestNote('q', clef, voice))
    }
  }

  return vexNotes
}

/** notesTrunc(원본 pitch/duration 배열)를 마디 내 [start,end) 박 구간 목록으로 변환 */
function beatRanges(notesTrunc) {
  let acc = 0
  return notesTrunc.map((n) => {
    const b = BEAT_VALUES[n.duration] ?? 1
    const r = { start: acc, end: acc + b, note: n }
    acc += b
    return r
  })
}

const DIATONIC_STEP_INDEX = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 }

/**
 * 오선 상 실제 위치(줄/칸) 거리를 반음이 아닌 음이름·옥타브 기준으로 계산.
 * 3도 이상은 임시표와 무관하게 줄/칸이 떨어져 있어 노트헤드가 겹치지 않으므로
 * 반음 거리로 판정하면(예: 단3도=3반음) 불필요하게 밀려 정렬이 어긋나 보인다.
 * 진짜 겹침(2도, 동일 위치)만 오프셋 대상으로 삼는다.
 */
function diatonicStepIndex(pitch) {
  const m = String(pitch).match(/^([A-G])(##|#|bb|b|n)?(\d+)$/)
  if (!m) return null
  return parseInt(m[3], 10) * 7 + DIATONIC_STEP_INDEX[m[1]]
}

/**
 * 위 성부 음표가 같은 박에 걸치는 아래 성부 음표와 오선 상 인접 위치(2도,
 * stepDist===1)에 있으면 위 성부 음표를 오른쪽으로 살짝 밀어 노트헤드끼리
 * 겹치지 않게 한다. 대(stem)가 있으면 꼬리/빔이 아래 성부 머리를 가리는
 * 것까지 막고, 온음표처럼 대가 없어도 노트헤드 타원 자체가 2도 간격에서는
 * 서로 겹치므로(악전상 이럴 땐 반드시 어긋내어 둘 다 보이게 해야 함) 똑같이
 * 민다 — 대 유무와 무관하게 적용한다.
 * 완전히 같은 음(유니즌, stepDist===0)은 밀지 않는다 — 유니즌은 VexFlow가
 * 자동으로 노트헤드 하나로 겹쳐 그려주므로(Tables.UNISON), 여기서 밀면 오히려
 * 하나로 뭉쳐져야 할 음이 둘로 떨어져 보이는 버그가 된다.
 * upperVex[j]는 upperTrunc[j]와 인덱스가 1:1로 대응한다는 전제.
 */
function avoidStemHeadCollision(upperVex, upperTrunc, lowerTrunc, maxStepDistance = 1) {
  const lowerRanges = beatRanges(lowerTrunc).filter((r) => !r.note.isRest)
  const upperRanges = beatRanges(upperTrunc)
  for (let j = 0; j < upperRanges.length; j++) {
    const u = upperRanges[j]
    if (u.note.isRest) continue
    const note = upperVex[j]
    if (!note) continue
    const overlap = lowerRanges.find((l) => u.start < l.end - 1e-6 && l.start < u.end - 1e-6)
    if (!overlap) continue
    const upperStep = diatonicStepIndex(u.note.pitch)
    const lowerStep = diatonicStepIndex(overlap.note.pitch)
    if (upperStep == null || lowerStep == null) continue
    const stepDist = upperStep - lowerStep
    if (stepDist > 0 && stepDist <= maxStepDistance) {
      try {
        note.setXShift?.((note.getXShift?.() ?? 0) + 6)
      } catch { /* ignore */ }
    }
  }
}

/**
 * 두 성부가 완전히 같은 음(음이름·임시표·옥타브까지 동일)을 같은 박에 공유하면,
 * 노트헤드는 하나로 겹쳐 보이는데 임시표는 성부마다 따로 붙어서 두 개가 겹쳐
 * 찍히고, 그만큼 폭도 이중으로 잡아먹어 앞 마디를 침범하게 된다.
 * 아래 성부 쪽 임시표를 제거해 하나만 남긴다 — 포매팅(폭 계산) 전에 호출해야
 * 지운 임시표의 폭이 애초에 반영되지 않는다.
 */
function dedupeUnisonAccidentals(upperVex, upperTrunc, lowerVex, lowerTrunc) {
  const lowerRanges = beatRanges(lowerTrunc).filter((r) => !r.note.isRest)
  const upperRanges = beatRanges(upperTrunc)
  for (let j = 0; j < upperRanges.length; j++) {
    const u = upperRanges[j]
    if (u.note.isRest) continue
    const overlap = lowerRanges.find((l) => u.start < l.end - 1e-6 && l.start < u.end - 1e-6)
    if (!overlap || overlap.note.pitch !== u.note.pitch) continue
    const lowerIdx = lowerTrunc.indexOf(overlap.note)
    const lowerNote = lowerIdx >= 0 ? lowerVex[lowerIdx] : null
    const mods = lowerNote?.getModifiers?.()
    if (!mods) continue
    const accIdx = mods.findIndex((m) => m?.getCategory?.() === 'Accidental')
    if (accIdx >= 0) mods.splice(accIdx, 1)
  }
}

function getMeasureAnalysis(analysisResult, measureIndex) {
  const arr = analysisResult?.measures
  if (!arr?.length) return null
  const byNum = arr.find((m) => Number(m.measure) === measureIndex + 1)
  if (byNum) return byNum
  return arr[measureIndex] ?? null
}

function textWidth(ctx, text) {
  try {
    const m = ctx.measureText(text)
    return m?.width ?? m?.w ?? 0
  } catch {
    return 0
  }
}

/** i b6 4: b·⁶ 동일 sup 크기(6음 내림), ₄는 아래 */
const FIG64_SUP_SCALE = 0.62
const FIG64_GAP_SCALE = 0.07
const FIG64_SUP_RISE = 0.36
const FIG64_SUB_DROP = 0.12

function measureIB64FigureWidth(ctx, size) {
  const gap = size * FIG64_GAP_SCALE
  const supSize = size * FIG64_SUP_SCALE
  const gapB6 = supSize * 0.05
  ctx.setFont('Georgia', size, 'bold')
  const wi = textWidth(ctx, 'i')
  ctx.setFont('Georgia', supSize, 'bold')
  const wb = textWidth(ctx, 'b')
  const w6 = textWidth(ctx, '6')
  const w4 = textWidth(ctx, '4')
  const xB = 0
  const x6 = xB + wb + gapB6
  const xc = x6 + w6 / 2
  const x4 = xc - w4 / 2
  const leftFig = Math.min(xB, x4)
  const rightFig = Math.max(x6 + w6, x4 + w4)
  return wi + gap + (rightFig - leftFig)
}

/** i + (b)(6)/(4): 6·4는 같은 수직열, b는 6 왼쪽(윗줄만) */
function drawChordSymbolIB64Figure(ctx, cx, rowY, size) {
  const baseY = rowY + CHORD_ROMAN_Y
  const gap = size * FIG64_GAP_SCALE
  const supSize = size * FIG64_SUP_SCALE
  const gapB6 = supSize * 0.05
  const topY = baseY - size * FIG64_SUP_RISE
  const botY = baseY + size * FIG64_SUB_DROP

  ctx.setFont('Georgia', size, 'bold')
  const wi = textWidth(ctx, 'i')
  ctx.setFont('Georgia', supSize, 'bold')
  const wb = textWidth(ctx, 'b')
  const w6 = textWidth(ctx, '6')
  const w4 = textWidth(ctx, '4')
  const xB = 0
  const x6 = xB + wb + gapB6
  const xc = x6 + w6 / 2
  const x4 = xc - w4 / 2
  const leftFig = Math.min(xB, x4)
  const rightFig = Math.max(x6 + w6, x4 + w4)
  const figW = rightFig - leftFig
  const totalW = wi + gap + figW
  const xStart = cx - totalW / 2
  const figOrigin = xStart + wi + gap - leftFig

  ctx.setFont('Georgia', size, 'bold')
  ctx.fillText('i', xStart, baseY)

  ctx.setFont('Georgia', supSize, 'bold')
  ctx.fillText('b', figOrigin + xB, topY)
  ctx.fillText('6', figOrigin + x6, topY)
  ctx.fillText('4', figOrigin + x4, botY)
}

/** 피겨 열: 일반 숫자를 같은 x열에 위·아래로 배치 (RISE+DROP이 클수록 위·아래 숫자 간 여백) */
const FIG_STACK_SCALE = 0.6
const FIG_COL_GAP = 0.04
const FIG_STACK_RISE = 0.52
const FIG_STACK_DROP = 0.32
const FIG_SINGLE_SUP_RISE = 0.34

function measureChordPartsWidth(ctx, parts, size, figScale) {
  const figSize = size * figScale
  let w = 0
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]
    const prev = i > 0 ? parts[i - 1] : null
    const gap = i > 0 && (p.type !== 'text' || (prev && prev.type !== 'text'))
      ? size * FIG_COL_GAP
      : 0
    if (p.type === 'text') {
      ctx.setFont('Georgia', size, 'bold')
      w += gap + textWidth(ctx, p.text)
    } else if (p.type === 'stack') {
      ctx.setFont('Georgia', figSize, 'bold')
      if (p.topFlat) {
        const wTop = textWidth(ctx, p.top)
        const wBot = textWidth(ctx, p.bottom)
        const wFl = textWidth(ctx, p.topFlat)
        const digitCol = Math.max(wTop, wBot)
        const gapF = figSize * 0.08
        w += gap + wFl + gapF + digitCol
      } else {
        const fd = splitFiguredStackFlatDigitBottom(p.bottom)
        if (fd) {
          const wTop = textWidth(ctx, p.top)
          const wFlat = textWidth(ctx, fd.flat)
          const wDig = textWidth(ctx, fd.digit)
          const digitCol = Math.max(wTop, wDig)
          const gapFlat = figSize * 0.08
          w += gap + wFlat + gapFlat + digitCol
        } else {
          const col = Math.max(textWidth(ctx, p.top), textWidth(ctx, p.bottom))
          w += gap + col
        }
      }
    } else if (p.type === 'stack3') {
      ctx.setFont('Georgia', figSize, 'bold')
      const col = Math.max(
        textWidth(ctx, p.top),
        textWidth(ctx, p.mid),
        textWidth(ctx, p.bottom),
      )
      w += gap + col
    } else if (p.type === 'supDigit') {
      ctx.setFont('Georgia', figSize, 'bold')
      w += gap + textWidth(ctx, p.digit)
    }
  }
  return w
}

function drawChordPartsAtCx(ctx, cx, mainY, parts, size, figScale) {
  const figSize = size * figScale
  const totalW = measureChordPartsWidth(ctx, parts, size, figScale)
  let x = cx - totalW / 2
  ctx.setFillStyle('#1e3a8a')
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]
    const prev = i > 0 ? parts[i - 1] : null
    const gap = i > 0 && (p.type !== 'text' || (prev && prev.type !== 'text'))
      ? size * FIG_COL_GAP
      : 0
    x += gap
    if (p.type === 'text') {
      ctx.setFont('Georgia', size, 'bold')
      const tw = textWidth(ctx, p.text)
      ctx.fillText(p.text, x, mainY)
      x += tw
    } else if (p.type === 'stack') {
      ctx.setFont('Georgia', figSize, 'bold')
      const topY = mainY - size * FIG_STACK_RISE
      const botY = mainY + size * FIG_STACK_DROP
      if (p.topFlat) {
        const wTop = textWidth(ctx, p.top)
        const wBot = textWidth(ctx, p.bottom)
        const wFl = textWidth(ctx, p.topFlat)
        const digitCol = Math.max(wTop, wBot)
        const gapF = figSize * 0.08
        const xDigits = x + wFl + gapF
        const xDigitMid = xDigits + digitCol / 2
        ctx.fillText(p.topFlat, x, topY)
        ctx.fillText(p.top, xDigitMid - wTop / 2, topY)
        ctx.fillText(p.bottom, xDigitMid - wBot / 2, botY)
        x += wFl + gapF + digitCol
      } else {
        const fd = splitFiguredStackFlatDigitBottom(p.bottom)
        if (fd) {
          const wTop = textWidth(ctx, p.top)
          const wFlat = textWidth(ctx, fd.flat)
          const wDig = textWidth(ctx, fd.digit)
          const digitCol = Math.max(wTop, wDig)
          const gapFlat = figSize * 0.08
          const xDigits = x + wFlat + gapFlat
          const xDigitMid = xDigits + digitCol / 2
          ctx.fillText(p.top, xDigitMid - wTop / 2, topY)
          ctx.fillText(fd.flat, x, botY)
          ctx.fillText(fd.digit, xDigitMid - wDig / 2, botY)
          x += wFlat + gapFlat + digitCol
        } else {
          const wT = textWidth(ctx, p.top)
          const wB = textWidth(ctx, p.bottom)
          const colW = Math.max(wT, wB)
          const colMid = x + colW / 2
          ctx.fillText(p.top, colMid - wT / 2, topY)
          ctx.fillText(p.bottom, colMid - wB / 2, botY)
          x += colW
        }
      }
    } else if (p.type === 'stack3') {
      ctx.setFont('Georgia', figSize, 'bold')
      const topY = mainY - size * 0.62
      const midY = mainY
      const botY = mainY + size * 0.52
      const wT = textWidth(ctx, p.top)
      const wM = textWidth(ctx, p.mid)
      const wB = textWidth(ctx, p.bottom)
      const colW = Math.max(wT, wM, wB)
      const colMid = x + colW / 2
      ctx.fillText(p.top, colMid - wT / 2, topY)
      ctx.fillText(p.mid, colMid - wM / 2, midY)
      ctx.fillText(p.bottom, colMid - wB / 2, botY)
      x += colW
    } else if (p.type === 'supDigit') {
      ctx.setFont('Georgia', figSize, 'bold')
      const w = textWidth(ctx, p.digit)
      const y = mainY - size * FIG_SINGLE_SUP_RISE
      ctx.fillText(p.digit, x, y)
      x += w
    }
  }
}

/** 공통화음 전조 등 두 줄 병기 라벨(예: "C: v♭" / "F: ii")의 최대 가로 폭 */
function measureChordLinesWidth(ctx, lines, size, figScale) {
  let maxW = 0
  for (const line of lines) {
    let w
    if (line.mode === 'parts' && line.parts.length > 0) {
      w = measureChordPartsWidth(ctx, line.parts, size, figScale)
    } else {
      ctx.setFont('Georgia', size, 'bold')
      w = textWidth(ctx, line.raw ?? '')
    }
    if (w > maxW) maxW = w
  }
  return maxW
}

/**
 * 두 줄 병기 라벨을 cx 기준 가운데 정렬로 세로 스택 렌더.
 * 첫 줄(옛 조 표기)을 다른 화성기호들과 같은 mainY에 맞추고, 그 아래로 나머지 줄을
 * 쌓는다 — 화성기호는 항상 수평으로 같은 높이(mainY)에 정렬돼야 함.
 */
function drawChordLinesAtCx(ctx, cx, mainY, lines, size, figScale, lineGap) {
  let y = mainY
  for (const line of lines) {
    if (line.mode === 'parts' && line.parts.length > 0) {
      drawChordPartsAtCx(ctx, cx, y, line.parts, size, figScale)
    } else {
      ctx.setFont('Georgia', size, 'bold')
      const text = line.raw ?? ''
      const w = textWidth(ctx, text)
      ctx.fillText(text, cx - w / 2, y)
    }
    y += lineGap
  }
}

function chordLabelInnerMetrics(x, w, isFirstColInRow) {
  const padL = isFirstColInRow ? 52 : 18
  const padR = 14
  const innerW = Math.max(8, w - padL - padR)
  return { padL, padR, innerW, minX: x + padL, maxX: x + w - padR }
}

/** drawHarmonyChordAtCx와 동일 규칙으로 축소 후 가로 너비(px) — 충돌 레이아웃용 */
function measureHarmonyChordLayoutWidth(ctx, chordSymbol, maxW) {
  if (!isValidChordSymbol(chordSymbol)) return 0
  const roman = String(chordSymbol)
  const parsed = parseChordSymbolParts(roman)
  ctx.save()
  if (parsed.mode === 'ib64') {
    let size = 15
    ctx.setFont('Georgia', size, 'bold')
    let mw = measureIB64FigureWidth(ctx, size)
    while (mw > maxW && size > 10) {
      size -= 0.5
      ctx.setFont('Georgia', size, 'bold')
      mw = measureIB64FigureWidth(ctx, size)
    }
    ctx.restore()
    return mw
  }
  if (parsed.mode === 'lines') {
    let size = 11
    ctx.setFont('Georgia', size, 'bold')
    let mw = measureChordLinesWidth(ctx, parsed.lines, size, FIG_STACK_SCALE)
    while (mw > maxW && size > 7) {
      size -= 0.5
      ctx.setFont('Georgia', size, 'bold')
      mw = measureChordLinesWidth(ctx, parsed.lines, size, FIG_STACK_SCALE)
    }
    ctx.restore()
    return mw
  }
  const { parts } = parsed
  let size = 15
  ctx.setFont('Georgia', size, 'bold')
  let mw = measureChordPartsWidth(ctx, parts, size, FIG_STACK_SCALE)
  while (mw > maxW && size > 10) {
    size -= 0.5
    ctx.setFont('Georgia', size, 'bold')
    mw = measureChordPartsWidth(ctx, parts, size, FIG_STACK_SCALE)
  }
  ctx.restore()
  return mw
}

/**
 * 겹침만 피하면서 cx를 가능한 한 각 박의 anchorX(음뭉치 중심)에 맞춘다.
 * (기존 symmetric separate는 기호가 오른쪽으로 밀리기 쉬움)
 */
function placeHarmonyLabelsAnchored(items, boundLeft, boundRight, minGap = 8) {
  if (items.length === 0) return
  for (const it of items) {
    if (it.anchorX == null) it.anchorX = it.cx
  }
  items.sort((a, b) => a.anchorX - b.anchorX)
  if (items.length === 1) {
    const it = items[0]
    it.cx = Math.min(boundRight - it.hw, Math.max(boundLeft + it.hw, it.anchorX))
    return
  }
  for (let sweep = 0; sweep < 80; sweep++) {
    let moved = false
    let prevRight = boundLeft
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      const minCx = i === 0 ? boundLeft + it.hw : prevRight + minGap + it.hw
      const nx = Math.max(it.anchorX, minCx)
      if (Math.abs(nx - it.cx) > 0.15) {
        it.cx = nx
        moved = true
      }
      prevRight = it.cx + it.hw
    }
    const last = items[items.length - 1]
    if (last.cx + last.hw > boundRight) {
      const nx = boundRight - last.hw
      if (Math.abs(nx - last.cx) > 0.15) {
        last.cx = nx
        moved = true
      }
    }
    for (let i = items.length - 2; i >= 0; i--) {
      const L = items[i]
      const R = items[i + 1]
      const maxCx = R.cx - R.hw - minGap - L.hw
      if (L.cx > maxCx) {
        const nx = Math.max(boundLeft + L.hw, maxCx)
        if (Math.abs(nx - L.cx) > 0.15) {
          L.cx = nx
          moved = true
        }
      }
    }
    for (const it of items) {
      if (it.cx - it.hw < boundLeft) {
        it.cx = boundLeft + it.hw
        moved = true
      }
      if (it.cx + it.hw > boundRight) {
        it.cx = boundRight - it.hw
        moved = true
      }
    }
    if (!moved) break
  }
}

/**
 * padL 기반 minX는 실제 첫 음보다 오른쪽일 수 있음 → boundLeft가 앵커보다 오른쪽이 되어 기호가 전부 밀림.
 * 마디 내 앵커·반폭에 맞춰 좌우 경계를 넉넉히 잡는다.
 */
function harmonyLayoutHorizontalBounds(layoutItems, staveX, staveW, innerMinX, innerMaxX, margin) {
  let boundL = innerMinX + margin
  let boundR = innerMaxX - margin
  const anchors = layoutItems.map((it) => it.anchorX).filter((v) => v != null && Number.isFinite(v))
  const hws = layoutItems.map((it) => it.hw).filter((v) => Number.isFinite(v))
  const maxHw = hws.length ? Math.max(...hws) : 28
  if (anchors.length) {
    const minAnch = Math.min(...anchors)
    const maxAnch = Math.max(...anchors)
    boundL = Math.min(boundL, minAnch - maxHw - 6)
    boundR = Math.max(boundR, maxAnch + maxHw + 6)
  }
  boundL = Math.max(staveX + 2, boundL)
  boundR = Math.min(staveX + staveW - 2, boundR)
  if (boundR - boundL < 40) {
    boundL = staveX + 2
    boundR = staveX + staveW - 2
  }
  return { boundL, boundR }
}

/** kordAI 화성 분석용: 캔버스에 실제로 그릴 때와 동일한 상한으로 측정한 반폭(충돌 검출용, 약간 여유) */
function harmonyHalfExtentForLayout(ctx, chordSymbol, slotMaxW) {
  const mw = measureHarmonyChordLayoutWidth(ctx, chordSymbol, slotMaxW)
  if (mw <= 0) return 0
  return (mw / 2) * 1.22 + 16
}

/**
 * 마디 안 음표/쉼표 밀도에 맞는 최소 너비.
 * 고정 FIRST_W/REST_W만 쓰면 8분음표가 많을 때 VexFlow가 마디선 밖으로 배치한다.
 */
function minStaveWidthForNoteContent(voiceNoteLists, beatsPerMeasure, isFirstColInRow) {
  // 마디 첫 박에 임시표가 있으면(특히 두 성부 이상 겹치면) 스태프 자체 왼쪽 여백이
  // 모자라서 임시표가 앞 마디 쪽으로 삐져나간다 — 앞에 붙일 음표가 없어 포매터가
  // 밀어낼 공간을 못 만들기 때문에, 마디 폭 자체에 미리 여유를 더 준다.
  let firstBeatAccidentals = 0
  for (const notes of voiceNoteLists) {
    const first = (notes ?? []).find((n) => !n?.isRest)
    if (first?.pitch && /[#bn]/.test(first.pitch)) firstBeatAccidentals += 1
  }
  // 임시표 2개 이상이 겹치면 VexFlow가 서로 안 겹치게 가로로 어긋내어(stagger) 배치하는데,
  // 그 어긋난 폭까지 감안해야 한다 — 개당 11px로는 부족해서 여전히 마디 밖(앞 마디 쪽)으로
  // 삐져나오는 현상이 있었다(사용자 지침으로 확인). 임시표 1개는 16px, 2개 이상부턴 겹침
  // 어긋내기 몫으로 개당 20px씩 더 준다.
  const accidentalPad = firstBeatAccidentals <= 1
    ? firstBeatAccidentals * 16
    : 16 + (firstBeatAccidentals - 1) * 20
  const padL = (isFirstColInRow ? 78 : 14) + accidentalPad
  const padR = 16
  let maxContent = 0
  for (const notes of voiceNoteLists) {
    const truncated = truncateNotesForMeasure(notes ?? [], beatsPerMeasure)
    let w = 0
    for (const n of truncated) {
      const dur = String(n.duration || 'q')
      w += TICKABLE_W[dur] ?? 24
      if (!n.isRest && n.pitch && /[#bn]/.test(n.pitch)) w += 10
    }
    // 박자 부족분 채움 쉼표도 Formatter가 공간을 씀
    let used = 0
    for (const n of truncated) used += BEAT_VALUES[n.duration] ?? 1
    let rem = beatsPerMeasure - used
    while (rem > 0.001) {
      if (rem >= 3.999) { w += TICKABLE_W.wr; rem -= 4 }
      else if (rem >= 1.999) { w += TICKABLE_W.hr; rem -= 2 }
      else if (rem >= 0.999) { w += TICKABLE_W.qr; rem -= 1 }
      else if (rem >= 0.499) { w += TICKABLE_W['8r']; rem -= 0.5 }
      else { w += TICKABLE_W.qr; rem -= 1 }
    }
    if (w > maxContent) maxContent = w
  }
  if (maxContent <= 0) maxContent = TICKABLE_W.wr
  return Math.ceil(padL + maxContent + padR)
}

/**
 * 박 단위 화성 기호가 겹치지 않게 하기 위한 마디 최소 너비(px).
 * 박 간격을 균등(innerW/bpp)로 둘 때 인접 쌍 요구 간격을 만족해야 함.
 */
function minStaveWidthForChordAnalysis(ctx, beats, beatsPerMeasure, isFirstColInRow) {
  if (!beats?.length) return 0
  const padL = isFirstColInRow ? 52 : 18
  const padR = 14
  /** 레이아웃 예산용: 넉넉한 상한으로 측정해 과소평가 방지 */
  const measSlot = 420
  const items = []
  for (const ab of beats) {
    if (!isValidChordSymbol(ab.chordSymbol)) continue
    const hw = harmonyHalfExtentForLayout(ctx, ab.chordSymbol, measSlot)
    if (hw > 0) items.push({ beat: ab.beat, hw, sym: ab.chordSymbol })
  }
  if (items.length === 0) return 0
  items.sort((a, b) => a.beat - b.beat)
  const minGap = 34
  let maxBeatSlot = 0
  // 실제로 마디 안에 "독립적으로 자리를 잡아야 하는" 기호 개수 — 같은 기호가 반음표처럼
  // 연속된 박에 그대로 이어지는 구간은 하나로 친다(beatsPerMeasure 그대로 곱하면 4분음표
  // 4개 기준으로 항상 과대 산정돼, 반음표 2개짜리 마디도 필요 이상으로 넓어진다).
  let distinctSlots = items.length ? 1 : 0
  for (let i = 0; i < items.length - 1; i++) {
    const db = items[i + 1].beat - items[i].beat
    if (db > 0 && db <= 1.001 && items[i].sym !== items[i + 1].sym) {
      const need = items[i].hw + items[i + 1].hw + minGap
      maxBeatSlot = Math.max(maxBeatSlot, need)
    }
    if (items[i + 1].sym !== items[i].sym) distinctSlots += 1
  }
  const slots = Math.max(1, distinctSlots)
  const pairNeed = maxBeatSlot > 0 ? Math.ceil(padL + padR + slots * maxBeatSlot + 12) : 0
  // 인접 박과 안 겹치더라도, 기호 자체가 넓으면(예: 임시딸림/부속화음 로마숫자) 마디
  // 경계 밖으로 잘려 나가지 않도록 가장 넓은 단일 기호 기준의 최소 너비도 확보.
  const maxHw = Math.max(0, ...items.map((it) => it.hw))
  const singleNeed = maxHw > 0 ? Math.ceil(padL + padR + maxHw * 2) : 0
  return Math.max(pairNeed, singleNeed)
}

/** 배치 이후, 인접 기호 사이 가용 폭으로 maxW를 다시 잡아 과소축소·잔여 겹침을 줄인다. */
function recomputeHarmonyMaxWidthsAfterSeparate(items, minX, maxX, pad = 10) {
  if (items.length === 0) return
  items.sort((a, b) => a.cx - b.cx)
  for (let i = 0; i < items.length; i++) {
    const leftEdge = i === 0 ? minX : items[i - 1].cx + items[i - 1].hw
    const rightEdge = i === items.length - 1 ? maxX : items[i + 1].cx - items[i + 1].hw
    const avail = Math.max(24, rightEdge - leftEdge - pad * 2)
    items[i].maxW = Math.max(48, Math.min(320, avail))
  }
}

/** 로마 화성 기호(피겨 숫자는 수직 열로 직접 배치) */
/** 붙임줄이 화음 기호 텍스트와 절대 겹치지 않도록 좌우에 남기는 여유(px) — 위·아래첨자는
 *  measureText가 실폭보다 좁게 잴 수 있어 넉넉히 잡는다. */
const TIE_EDGE_MARGIN = 12

function drawHarmonyChordAtCx(ctx, cx, rowY, chordSymbol, maxW) {
  if (!isValidChordSymbol(chordSymbol)) return null
  const roman = String(chordSymbol)
  const mainY = rowY + CHORD_ROMAN_Y
  const parsed = parseChordSymbolParts(roman)

  if (parsed.mode === 'ib64') {
    ctx.save()
    let size = 15
    ctx.setFont('Georgia', size, 'bold')
    let mw = measureIB64FigureWidth(ctx, size)
    while (mw > maxW && size > 10) {
      size -= 0.5
      ctx.setFont('Georgia', size, 'bold')
      mw = measureIB64FigureWidth(ctx, size)
    }
    ctx.setFillStyle('#1e3a8a')
    drawChordSymbolIB64Figure(ctx, cx, rowY, size)
    ctx.restore()
    return { leftX: cx - mw / 2 - TIE_EDGE_MARGIN, rightX: cx + mw / 2 + TIE_EDGE_MARGIN, y: mainY }
  }

  if (parsed.mode === 'lines') {
    ctx.save()
    let size = 11
    ctx.setFont('Georgia', size, 'bold')
    let mw = measureChordLinesWidth(ctx, parsed.lines, size, FIG_STACK_SCALE)
    while (mw > maxW && size > 7) {
      size -= 0.5
      ctx.setFont('Georgia', size, 'bold')
      mw = measureChordLinesWidth(ctx, parsed.lines, size, FIG_STACK_SCALE)
    }
    ctx.setFillStyle('#1e3a8a')
    // 두 줄 다 전위 피겨(⁶₃ 등) 스택을 가질 수 있어(예: "Bb: V⁶₃\nC: IV⁶₃"), 스택 자체의
    // 세로 폭(위로 size*0.62, 아래로 size*0.52)이 겹치지 않도록 충분히 띄운다.
    const lineGap = size * 1.9
    drawChordLinesAtCx(ctx, cx, mainY, parsed.lines, size, FIG_STACK_SCALE, lineGap)
    ctx.restore()
    // 붙임줄은 아래 줄(새 조 표기 — 이 화음부터 이어지는 해석)에서 출발해서, 다른
    // 화성기호와 같은 높이(mainY, 첫 줄 높이)로 꺾여 올라간 뒤 다음 화음으로 이어진다.
    const bottomLineY = mainY + (parsed.lines.length - 1) * lineGap
    return { leftX: cx - mw / 2 - TIE_EDGE_MARGIN, rightX: cx + mw / 2 + TIE_EDGE_MARGIN, y: bottomLineY }
  }

  const { parts } = parsed
  ctx.save()
  let size = 15
  ctx.setFont('Georgia', size, 'bold')
  let mw = measureChordPartsWidth(ctx, parts, size, FIG_STACK_SCALE)
  while (mw > maxW && size > 10) {
    size -= 0.5
    ctx.setFont('Georgia', size, 'bold')
    mw = measureChordPartsWidth(ctx, parts, size, FIG_STACK_SCALE)
  }
  ctx.setFillStyle('#1e3a8a')
  drawChordPartsAtCx(ctx, cx, mainY, parts, size, FIG_STACK_SCALE)
  ctx.restore()
  return { leftX: cx - mw / 2 - TIE_EDGE_MARGIN, rightX: cx + mw / 2 + TIE_EDGE_MARGIN, y: mainY }
}

/** 화살촉(오른쪽을 가리킴)을 tipX,tipY에 그린다 — drawModulationTie·drawModulationTieRowEdges 공용. */
function drawRightArrowhead(ctx, tipX, tipY, color) {
  const ah = 5
  ctx.setFillStyle(color)
  ctx.beginPath()
  ctx.moveTo(tipX, tipY)
  ctx.lineTo(tipX - ah, tipY - ah * 0.62)
  ctx.lineTo(tipX - ah, tipY + ah * 0.62)
  ctx.closePath()
  ctx.fill()
}

/**
 * 공통화음 전조 붙임줄 — 피벗 화음(두 줄 병기)의 오른쪽 가장자리에서 짧게 나왔다가
 * 위로 오르고, 다시 오른쪽으로 뻗어 다음 화음 왼쪽 가장자리에 화살촉으로 꽂히는
 * 직각(ㄴ+ㄱ) 커넥터. 텍스트 폭에 여유 마진을 둔 leftX/rightX를 쓰므로 표기와
 * 절대 겹치지 않는다. cur.raw가 '^'로 시작할 때만 호출됨.
 * 같은 줄(rowY 동일)일 때만 그린다 — 시스템(줄)이 바뀌면 drawModulationTieRowEdges가 대신 그림.
 */
function drawModulationTie(ctx, prev, cur) {
  if (!prev || !cur || prev.rowY !== cur.rowY) return
  const color = '#7c3aed'
  const startX = prev.rightX
  const startY = prev.y
  const endX = cur.leftX
  // cur.y는 텍스트 베이스라인이라 그대로 쓰면 화살촉이 글자 아래쪽을 가리키게 됨 —
  // 글자 세로 중앙 쪽을 가리키도록 살짝 더 올린다.
  const endY = cur.y - 7
  const riserX = Math.min(startX + Math.max(10, Math.min(22, (endX - startX) * 0.3)), endX - 15)

  ctx.save()
  ctx.setStrokeStyle(color)
  ctx.setLineWidth(1.3)
  ctx.setLineCap?.('square')
  ctx.setLineJoin?.('round')
  ctx.beginPath()
  ctx.moveTo(startX, startY)
  ctx.lineTo(riserX, startY)
  ctx.lineTo(riserX, endY)
  ctx.lineTo(endX, endY)
  ctx.stroke()

  drawRightArrowhead(ctx, endX, endY, color)
  ctx.restore()
}

/**
 * 공통화음 전조 붙임줄 — 피벗과 확정 화음이 서로 다른 줄(시스템)에 걸릴 때.
 * 한 줄로 이어 그릴 수 없으니, 피벗 쪽 줄 오른쪽 끝까지 짧게 뻗어 화살촉으로 "다음 줄로
 * 이어짐"만 표시한다 — 확정 화음 쪽(다음 줄 시작)은 '^' 접두 자체가 이미 표시라 화살표 생략.
 */
function drawModulationTieRowEdges(ctx, prev, cur, rowRightEdgeX) {
  if (!prev || !cur || prev.rowY === cur.rowY) return
  const color = '#7c3aed'
  // prev.y가 두 줄 병기의 아래 줄(새 조 표기) 높이일 수 있으므로, 그 줄의 기본 화성기호
  // 높이(mainY)까지 정확히 올라오게 — 다른 화성기호와 항상 같은 높이로 이어지도록.
  const rowMainY = prev.rowY + CHORD_ROMAN_Y
  const riserUpY = Math.max(0, prev.y - rowMainY)

  ctx.save()
  ctx.setStrokeStyle(color)
  ctx.setLineWidth(1.3)
  ctx.setLineCap?.('square')
  ctx.setLineJoin?.('round')

  // 피벗 쪽: 오른쪽으로 짧게 나왔다가 위로 살짝 꺾어, 그 줄 실제 오른쪽 끝(오선 끝)까지.
  const tailRiserX = prev.rightX + 12
  const tailY = prev.y - riserUpY
  const tailEndX = Math.max(tailRiserX + 8, rowRightEdgeX)
  ctx.beginPath()
  ctx.moveTo(prev.rightX, prev.y)
  ctx.lineTo(tailRiserX, prev.y)
  ctx.lineTo(tailRiserX, tailY)
  ctx.lineTo(tailEndX, tailY)
  ctx.stroke()
  drawRightArrowhead(ctx, tailEndX, tailY, color)

  ctx.restore()
}

/** VexFlow: 음표 머리의 가로 중심(화성 기호를 음뭉치 중앙에 맞추기 위함) */
function staveNoteHeadCenterX(vn) {
  if (!vn) return null
  try {
    if (typeof vn.getNoteHeadBeginX === 'function' && typeof vn.getNoteHeadEndX === 'function') {
      const b = vn.getNoteHeadBeginX()
      const e = vn.getNoteHeadEndX()
      if (Number.isFinite(b) && Number.isFinite(e)) {
        if (Math.abs(e - b) > 0.25) return (b + e) / 2
        return b
      }
    }
  } catch { /* ignore */ }
  try {
    if (typeof vn.getAbsoluteX !== 'function') return null
    const ax = vn.getAbsoluteX()
    if (!Number.isFinite(ax)) return null
    let durStr = 'q'
    try { durStr = (vn.getDuration?.() ?? 'q').replace('r', '') } catch { /* ignore */ }
    const half = durStr === 'w' ? 14 : durStr === 'h' ? 9 : durStr === 'q' ? 5.5 : 5
    return ax + half
  } catch { /* ignore */ }
  return null
}

/**
 * Beat index (0..bpp-1) -> note-head X at the same sample time as
 * harmony_engine.tokens_at_beat (t = min(beatIdx + 0.5, bpp - eps)).
 */
function buildBeatCenterXMap(vexNotes, beatsPerMeasure) {
  const spans = []
  let cumBeats = 0
  for (const vn of vexNotes) {
    let durStr = 'q'
    let dots = 0
    try { durStr = (vn.getDuration?.() ?? 'q').replace('r', '') } catch { /* ignore */ }
    try { dots = vn.getDots?.() ?? 0 } catch { /* ignore */ }
    let durBeats = BEAT_VALUES[durStr] ?? 1
    for (let d = 0; d < dots; d++) durBeats *= 1.5
    const isRest = (() => { try { return vn.isRest?.() ?? false } catch { return false } })()
    const start = cumBeats
    const end = cumBeats + durBeats
    const cx = !isRest ? staveNoteHeadCenterX(vn) : null
    spans.push({ start, end, cx, isRest })
    cumBeats = end
  }
  const map = new Map()
  const bpp = Math.max(1, beatsPerMeasure)
  for (let beatIdx = 0; beatIdx < bpp; beatIdx++) {
    const t = Math.min(beatIdx + 0.5, bpp - BEAT_SAMPLE_EPS)
    for (const s of spans) {
      if (s.isRest || s.cx == null) continue
      if (s.start <= t + BEAT_SAMPLE_EPS && t < s.end + BEAT_SAMPLE_EPS) {
        map.set(beatIdx, s.cx)
        break
      }
    }
  }
  return map
}

/** 4성부 음표 머리 x 중 최소·최대의 중점 = 세로 화음의 가로 중심에 가깝다 */
function chordBeatAnchorXFromMaps(beat, ...maps) {
  const xs = []
  for (const m of maps) {
    const v = m.get(beat)
    if (v != null && Number.isFinite(v)) xs.push(v)
  }
  if (xs.length === 0) return null
  if (xs.length === 1) return xs[0]
  let lo = xs[0]
  let hi = xs[0]
  for (let k = 1; k < xs.length; k++) {
    if (xs[k] < lo) lo = xs[k]
    if (xs[k] > hi) hi = xs[k]
  }
  return (lo + hi) / 2
}

function isValidChordSymbol(sym) {
  if (!sym || sym === '—' || sym === '-' || sym === '?') return false
  if (String(sym).startsWith('incomplete')) return false
  return true
}

const ISSUE_VOICE_MAP = {
  소프라노: 'soprano',
  알토: 'alto',
  테너: 'tenor',
  베이스: 'bass',
}

const VOICE_ORDER = ['soprano', 'alto', 'tenor', 'bass']
const VOICE_SHORT = { soprano: 'S', alto: 'A', tenor: 'T', bass: 'B' }

/** 성부쌍을 SATB 순서로 정렬한 "S-A" 형태 약칭으로. */
function voicePairShort(pair) {
  const [v1, v2] = pair
  const sorted = [v1, v2].sort((a, b) => VOICE_ORDER.indexOf(a) - VOICE_ORDER.indexOf(b))
  return sorted.map((v) => VOICE_SHORT[v] ?? '?').join('-')
}

function voiceLeadingIssueText(iss) {
  if (iss == null) return ''
  if (typeof iss === 'string') return iss
  if (typeof iss === 'object') {
    const m = iss.message ?? iss.text ?? iss.msg
    if (typeof m === 'string') return m
  }
  return String(iss)
}

function parseParallelIssueVoicePair(issueText) {
  const txt = voiceLeadingIssueText(issueText)
  if (!txt.includes('병행') && !txt.includes('은복')) return null
  const m = txt.match(/(소프라노|알토|테너|베이스)\s*[\u2013\u2014\-]\s*(소프라노|알토|테너|베이스)/)
  if (!m) return null
  const v1 = ISSUE_VOICE_MAP[m[1]]
  const v2 = ISSUE_VOICE_MAP[m[2]]
  if (!v1 || !v2) return null
  return [v1, v2]
}

function parseParallelIssueLevel(issueText) {
  const txt = voiceLeadingIssueText(issueText)
  if (!txt.includes('병행') && !txt.includes('은복')) return null
  if (txt.includes('의심')) return 'suspected'
  if (txt.includes('확실') || txt.includes('발견') || txt.includes('검출')) return 'certain'
  return 'certain'
}

/** '5' | '8' | null */
function parseParallelIssueInterval(issueText) {
  const txt = voiceLeadingIssueText(issueText)
  if (txt.includes('8도') || txt.includes('완전8')) return '8'
  if (txt.includes('5도') || txt.includes('완전5') || txt.includes('감5') || txt.includes('부등5')) return '5'
  return null
}

function parseParallelIssueKind(issueText) {
  const txt = voiceLeadingIssueText(issueText)
  if (txt.includes('은복')) return 'hidden'
  if (txt.includes('감5도→완5') || txt.includes('감5→완5') || txt.includes('부등')) return 'unequal'
  if (txt.includes('반진행')) return 'contrary'
  return 'direct'
}

function shortParallelLabel(kind, interval) {
  if (kind === 'hidden') return interval === '8' ? '은복8' : '은복5'
  if (kind === 'unequal') return '감5→완5'
  if (kind === 'contrary') return interval === '8' ? '반병행8' : '반병행5'
  return interval === '8' ? '병행8' : '병행5'
}

/** Analysis beat[] -> sorted {start,end} spans in this measure's beat space */
function beatBlocksToSpanBlocks(beats, beatsPerMeasure) {
  if (!beats?.length) return []
  const beatNums = beats
    .map((b) => Number(b?.beat))
    .filter((v) => Number.isFinite(v))
  const beatBaseOffset = beatNums.length > 0 && Math.min(...beatNums) >= 1 ? 1 : 0
  const toBeat0 = (v) => {
    const n = Number(v)
    if (!Number.isFinite(n)) return 0
    return Math.min(beatsPerMeasure, Math.max(0, n - beatBaseOffset))
  }
  return [...beats]
    .map((b) => {
      const start = toBeat0(b?.beat)
      const end = Math.max(start + 0.001, toBeat0(b?.beatEnd ?? (Number(b?.beat) + 1)))
      return { b, start, end }
    })
    .sort((x, y) => x.start - y.start)
}

function collectParallelIssueSpans(aResult, beatsPerMeasure) {
  const blocks = beatBlocksToSpanBlocks(aResult?.beats, beatsPerMeasure)
  if (blocks.length === 0) return []

  const spans = []
  for (let i = 0; i < blocks.length; i++) {
    const { b, start, end } = blocks[i]
    const issues = Array.isArray(b?.voiceLeadingIssues) ? b.voiceLeadingIssues : []
    if (issues.length === 0) continue
    for (const iss of issues) {
      const pair = parseParallelIssueVoicePair(iss)
      if (!pair) continue
      const level = parseParallelIssueLevel(iss)
      if (!level) continue
      const voices = new Set(pair)
      spans.push({ start, end, voices, level })
      if (i > 0) {
        const prev = blocks[i - 1]
        spans.push({ start: prev.start, end: prev.end, voices, level })
      }
    }
  }
  return spans
}

/**
 * Cross-measure parallel flags sit on the NEXT measure's first beat block (prevMidis).
 * Spans are always in the measure being drawn: if the next measure's first block has
 * a parallel issue, add a span for THIS measure's last beat block (same beat coords).
 */
function collectParallelIssueSpansForMeasure(analysisResult, measureIndex, beatsPerMeasure) {
  const aResult = getMeasureAnalysis(analysisResult, measureIndex)
  const spans = collectParallelIssueSpans(aResult, beatsPerMeasure)

  const nextResult = getMeasureAnalysis(analysisResult, measureIndex + 1)
  if (!nextResult?.beats?.length || !aResult?.beats?.length) return spans

  const nextFirstIssues = nextResult.beats[0]?.voiceLeadingIssues
  if (!Array.isArray(nextFirstIssues) || nextFirstIssues.length === 0) return spans

  const blocksHere = beatBlocksToSpanBlocks(aResult.beats, beatsPerMeasure)
  if (blocksHere.length === 0) return spans
  const { start: lastS, end: lastE } = blocksHere[blocksHere.length - 1]

  for (const iss of nextFirstIssues) {
    const pair = parseParallelIssueVoicePair(iss)
    if (!pair) continue
    const level = parseParallelIssueLevel(iss)
    if (!level) continue
    spans.push({ start: lastS, end: lastE, voices: new Set(pair), level })
  }
  return spans
}

function noteOverlapsSpan(noteStart, noteEnd, spanStart, spanEnd, eps = 1e-4) {
  return noteStart < spanEnd - eps && spanStart < noteEnd - eps
}

function applyParallelIssueStylesToVoice(vexArr, notes, voice, parallelSpans, beatsPerMeasure) {
  if (!parallelSpans.length) return
  let beatCursor = 0
  const bpp = Math.max(1, beatsPerMeasure)
  for (let j = 0; j < notes.length && j < vexArr.length; j++) {
    const n = notes[j]
    const vn = vexArr[j]
    const dur = vexNoteBeats(vn)
    const noteStart = beatCursor
    const noteEnd = Math.min(bpp, beatCursor + dur)
    if (!n.isRest) {
      for (const span of parallelSpans) {
        if (!span.voices.has(voice)) continue
        if (!noteOverlapsSpan(noteStart, noteEnd, span.start, span.end)) continue
        // 병행/은복 사선·텍스트와 같은 레드 계열로 통일 — 노트헤드·스템도 같이 물들인다.
        const fill = '#b45309'
        try {
          const st = { fillStyle: fill, strokeStyle: fill }
          if (typeof vn.setGroupStyle === 'function') vn.setGroupStyle(st)
          else vn.setStyle(st)
        } catch { /* ignore */ }
        break
      }
    }
    beatCursor = Math.min(bpp, beatCursor + dur)
  }
}

/**
 * 병행 전이(이전 화음 → 현재 화음). 시각 라벨·선용.
 * prev가 null이면 마디 첫 박(직전 마디에서 이어짐).
 */
function collectParallelTransitions(aResult, beatsPerMeasure) {
  const blocks = beatBlocksToSpanBlocks(aResult?.beats, beatsPerMeasure)
  if (blocks.length === 0) return []
  const out = []
  for (let i = 0; i < blocks.length; i++) {
    const { b, start, end } = blocks[i]
    const issues = Array.isArray(b?.voiceLeadingIssues) ? b.voiceLeadingIssues : []
    if (issues.length === 0) continue
    for (const iss of issues) {
      const pair = parseParallelIssueVoicePair(iss)
      if (!pair) continue
      const interval = parseParallelIssueInterval(iss)
      if (!interval) continue
      const kind = parseParallelIssueKind(iss)
      const prev = i > 0
        ? { start: blocks[i - 1].start, end: blocks[i - 1].end }
        : null
      out.push({
        prev,
        curr: { start, end },
        voices: pair,
        interval,
        kind,
        label: `${voicePairShort(pair)} ${shortParallelLabel(kind, interval)}`,
      })
    }
  }
  return out
}

function buildVoiceHeadSpans(vexArr, notes, beatsPerMeasure) {
  const spans = []
  let beatCursor = 0
  const bpp = Math.max(1, beatsPerMeasure)
  // VexFlow getAbsoluteX ≈ 음머리 왼쪽; 시각 중심까지 보정
  const NOTE_HEAD_CX = 5.8
  for (let j = 0; j < notes.length && j < vexArr.length; j++) {
    const n = notes[j]
    const vn = vexArr[j]
    const dur = vexNoteBeats(vn)
    const noteStart = beatCursor
    const noteEnd = Math.min(bpp, beatCursor + dur)
    if (!n.isRest) {
      try {
        const x = vn.getAbsoluteX?.()
        const y = vn.getYs?.()?.[0]
        if (Number.isFinite(x) && Number.isFinite(y)) {
          spans.push({ start: noteStart, end: noteEnd, x: x + NOTE_HEAD_CX, y })
        }
      } catch { /* ignore */ }
    }
    beatCursor = Math.min(bpp, beatCursor + dur)
  }
  return spans
}

function headNearBeatSpan(spans, beatStart, beatEnd) {
  if (!spans?.length) return null
  const mid = (beatStart + beatEnd) / 2
  let best = null
  let bestDist = Infinity
  for (const s of spans) {
    if (!noteOverlapsSpan(s.start, s.end, beatStart, beatEnd)) continue
    const d = Math.abs((s.start + s.end) / 2 - mid)
    if (d < bestDist) {
      bestDist = d
      best = s
    }
  }
  return best
}

/**
 * 음머리 중심 → 목표점 방향으로, 타원형 음머리 가장자리(+여유) 좌표.
 */
function noteHeadEdgeToward(from, toward, gap = 4) {
  if (!from || !toward) return from
  const dx = toward.x - from.x
  const dy = toward.y - from.y
  const len = Math.hypot(dx, dy)
  if (len < 1e-6) return { x: from.x, y: from.y }
  const ux = dx / len
  const uy = dy / len
  const rx = 7.2
  const ry = 5.2
  const edge = 1 / Math.sqrt((ux / rx) ** 2 + (uy / ry) ** 2)
  const t = Math.min(len * 0.45, edge + gap)
  return { x: from.x + ux * t, y: from.y + uy * t }
}

/**
 * 두 성부의 이전→현재 음머리를 잇는 단순 사선 + 짧은 라벨.
 * 시작점(이전 화음) 쪽 여유를 더 크게 둬 음머리와 붙지 않게 한다.
 */
function drawParallelMotionMark(ctx, {
  p1, p2, c1, c2, label, rowY, labelStackIndex = 0, kind = 'direct',
}) {
  if (!p1 || !p2 || !c1 || !c2) return
  // 은복(hidden)과 진짜 병행(direct)은 같은 레드 계열 색으로 통일하고,
  // 선 모양(점선 vs 실선)으로만 구분한다.
  const color = '#b45309'
  const dash = kind === 'hidden' ? [4, 3] : []
  // 시작(이전) 쪽 gap을 더 크게 — 끝(현재)보다 공간을 넉넉히
  const a1 = noteHeadEdgeToward(p1, c1, 7.5)
  const b1 = noteHeadEdgeToward(c1, p1, 4.5)
  const a2 = noteHeadEdgeToward(p2, c2, 7.5)
  const b2 = noteHeadEdgeToward(c2, p2, 4.5)

  ctx.save()
  ctx.setStrokeStyle(color)
  ctx.setFillStyle(color)
  ctx.setLineWidth(1.15)
  ctx.setLineCap?.('round')
  try { ctx.setLineDash?.(dash) } catch { /* ignore */ }

  ctx.beginPath()
  ctx.moveTo(a1.x, a1.y)
  ctx.lineTo(b1.x, b1.y)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(a2.x, a2.y)
  ctx.lineTo(b2.x, b2.y)
  ctx.stroke()

  const mx = (a1.x + a2.x + b1.x + b2.x) / 4
  const ys = [p1.y, p2.y, c1.y, c2.y]
  const yMin = Math.min(...ys)
  const gapY = rowY + GRAND_STAFF_SPLIT_Y
  const trebleTop = rowY + TREBLE_Y - 8
  const allBass = ys.every((y) => y > gapY + 4)

  // 마디 위쪽 빈 공간에 표시 — 같은 전이에 병행+은복처럼 라벨이 여럿이면 나열된
  // 순서 그대로 위에서 아래로 쌓는다(먼저 나온 게 위, 그다음이 바로 밑).
  const stackGap = 11
  let labelY
  if (allBass) {
    labelY = gapY + 2 + labelStackIndex * stackGap
  } else {
    labelY = Math.min(trebleTop - 2, yMin - 20) + labelStackIndex * stackGap
  }

  ctx.setFont('Arial', 9, 'normal')
  let tw = label.length * 5.5
  try {
    if (typeof ctx.measureText === 'function') tw = ctx.measureText(label).width
  } catch { /* ignore */ }
  ctx.fillText(label, mx - tw / 2, labelY)
  ctx.restore()
}

/**
 * 줄(Staff/시스템)이 바뀌어 이전↔현재 음표를 하나의 사선으로 잇지 못할 때,
 * 해당 줄 쪽 음머리에서 줄 끝(dir=1) 또는 줄 시작(dir=-1) 방향으로 일반
 * 병행선과 같은 길이의 실선 + 라벨을 남겨 병행이 줄을 건너 이어짐을 표시한다.
 */
function drawParallelRowEdgeMark(ctx, { h1, h2, dir, label, rowY }) {
  if (!h1 || !h2) return
  const color = '#b45309'
  const stubLen = 48
  ctx.save()
  ctx.setStrokeStyle(color)
  ctx.setFillStyle(color)
  ctx.setLineWidth(1.15)
  ctx.setLineCap?.('round')

  for (const h of [h1, h2]) {
    const start = noteHeadEdgeToward(h, { x: h.x + dir * 100, y: h.y }, dir > 0 ? 7.5 : 4.5)
    ctx.beginPath()
    ctx.moveTo(start.x, start.y)
    ctx.lineTo(start.x + dir * stubLen, start.y)
    ctx.stroke()
  }

  const ys = [h1.y, h2.y]
  const yMin = Math.min(...ys)
  const gapY = rowY + GRAND_STAFF_SPLIT_Y
  const trebleTop = rowY + TREBLE_Y - 8
  const allBass = ys.every((y) => y > gapY + 4)
  let labelY = allBass ? gapY + 2 : Math.min(trebleTop - 2, yMin - 12)
  labelY = Math.max(rowY + 8, labelY)

  ctx.setFont('Arial', 9, 'normal')
  const text = dir > 0 ? `${label} →` : `→ ${label}`
  let tw = text.length * 5.5
  try {
    if (typeof ctx.measureText === 'function') tw = ctx.measureText(text).width
  } catch { /* ignore */ }
  const mx = (h1.x + h2.x) / 2 + dir * (stubLen + 4)
  const tx = dir > 0 ? mx : mx - tw
  ctx.fillText(text, tx, labelY)
  ctx.restore()
}

function resolveParallelHeads(headsByVoice, voices, beatSpan) {
  if (!beatSpan) return null
  const [v1, v2] = voices
  const h1 = headNearBeatSpan(headsByVoice[v1], beatSpan.start, beatSpan.end)
  const h2 = headNearBeatSpan(headsByVoice[v2], beatSpan.start, beatSpan.end)
  if (!h1 || !h2) return null
  return { p1: h1, p2: h2 }
}

function beatIndexForHarmonyBlock(ab, beatBaseOffset, beatsPerMeasure) {
  const bpp = Math.max(1, beatsPerMeasure)
  const raw = Number(ab?.beat)
  if (!Number.isFinite(raw)) return 0
  return Math.min(bpp - 1, Math.max(0, raw - beatBaseOffset))
}

export default function ScoreRenderer({
  score,
  keySignature,
  timeSignature,
  activeVoice: _activeVoice,
  analysisResult,
  playbackCursor,
  onNoteChange,
  onNoteDelete,
  onMeasureClear,
  onDragStart,
  selectedNote,
  onSelectNote,
  selectedMeasure,
  onSelectMeasure,
  theoryBase = 'baek',
}) {
  const containerRef = useRef(null)
  const wrapRef = useRef(null)
  const noteHitMapRef = useRef([])
  const measureBoundsRef = useRef([])
  const [containerWidth, setContainerWidth] = useState(800)
  const [zoom, setZoom] = useState(1)
  const zoomRef = useRef(1)
  zoomRef.current = zoom

  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver((entries) => {
      setContainerWidth(entries[0]?.contentRect?.width ?? 800)
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  // Ctrl+마우스 휠로 악보 확대/축소 (레이아웃 자체는 그대로, 시각적 배율만 조정)
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const onWheel = (e) => {
      if (!e.ctrlKey) return
      e.preventDefault()
      const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08
      setZoom((z) => Math.min(3, Math.max(0.4, z * factor)))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key !== 'Delete' && e.key !== 'Backspace') return

      if (
        selectedMeasure != null
        && Number.isFinite(Number(selectedMeasure.measureIndex))
      ) {
        e.preventDefault()
        onMeasureClear?.(selectedMeasure.measureIndex, selectedMeasure.staff)
        onSelectMeasure?.(null)
        onSelectNote?.(null)
        return
      }

      if (!selectedNote || selectedNote.isRest || selectedNote.noteIndex == null) return
      e.preventDefault()
      onNoteDelete?.(selectedNote.voice, selectedNote.noteIndex)
      onSelectNote?.(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedNote, selectedMeasure, onNoteDelete, onSelectNote, onMeasureClear, onSelectMeasure])

  useEffect(() => {
    const onArrow = (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      if (!selectedNote || selectedNote.isRest || selectedNote.noteIndex == null || !selectedNote.pitch) return
      e.preventDefault()
      const steps = e.key === 'ArrowUp' ? 1 : -1
      // Ctrl/Cmd+화살표: 시벨리우스처럼 온음계 1도가 아니라 옥타브(같은 음이름) 단위로 이동
      let newPitch
      if (e.ctrlKey || e.metaKey) {
        const m = String(selectedNote.pitch).match(/^([A-G])(##|#|bb|b|n)?(-?\d+)$/)
        newPitch = m ? `${m[1]}${m[2] ?? ''}${parseInt(m[3], 10) + steps}` : selectedNote.pitch
      } else {
        newPitch = shiftPitchDiatonic(selectedNote.pitch, steps, keySignature)
      }
      onDragStart?.()
      onNoteChange?.(selectedNote.voice, selectedNote.noteIndex, newPitch)
      onSelectNote?.({ ...selectedNote, pitch: newPitch })
    }
    window.addEventListener('keydown', onArrow)
    return () => window.removeEventListener('keydown', onArrow)
  }, [selectedNote, keySignature, onNoteChange, onDragStart, onSelectNote])

  const handleMouseDown = useCallback(
    (e) => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const z = zoomRef.current
      const px = (e.clientX - rect.left) / z
      const py = (e.clientY - rect.top) / z

      let best = null
      let bestD = 16
      for (const h of noteHitMapRef.current) {
        const d = Math.hypot(h.x - px, h.y - py)
        if (d < bestD) {
          bestD = d
          best = h
        }
      }

      if (best) {
        onSelectMeasure?.(null)
        onSelectNote?.({
          voice: best.voice,
          flatIndex: best.flatIndex,
          noteIndex: best.isRest ? undefined : best.noteIndex,
          isRest: best.isRest,
          pitch: best.pitch,
          duration: best.duration,
          isFiller: best.isFiller,
          padEntries: best.padEntries,
        })
        if (!best.isRest && best.noteIndex != null && e.button === 0) {
          const origPitch = best.pitch
          const startY = e.clientY
          let lastSteps = 0
          let historyPushed = false
          const onMove = (ev) => {
            const steps = Math.round((startY - ev.clientY) / (DRAG_STEP_PX * z))
            if (steps === lastSteps) return
            if (!historyPushed && steps !== 0) {
              onDragStart?.()
              historyPushed = true
            }
            lastSteps = steps
            const newPitch = shiftPitchDiatonic(origPitch, steps, keySignature)
            onNoteChange?.(best.voice, best.noteIndex, newPitch)
          }
          const onUp = () => {
            document.removeEventListener('mousemove', onMove)
            document.removeEventListener('mouseup', onUp)
          }
          document.addEventListener('mousemove', onMove)
          document.addEventListener('mouseup', onUp)
        }
        return
      }

      if (e.ctrlKey) {
        for (const b of measureBoundsRef.current) {
          if (px >= b.x1 && px <= b.x2 && py >= b.y1 && py <= b.y2) {
            const staff = py < b.splitY ? 'upper' : 'lower'
            onMeasureClear?.(b.measureIndex, staff)
            onSelectNote?.(null)
            onSelectMeasure?.(null)
            return
          }
        }
      }

      for (const b of measureBoundsRef.current) {
        if (px >= b.x1 && px <= b.x2 && py >= b.y1 && py <= b.y2) {
          const staff = py < b.splitY ? 'upper' : 'lower'
          onSelectMeasure?.({ measureIndex: b.measureIndex, staff })
          onSelectNote?.(null)
          return
        }
      }

      onSelectNote?.(null)
      onSelectMeasure?.(null)
    },
    [keySignature, onSelectNote, onNoteChange, onDragStart, onMeasureClear, onSelectMeasure],
  )

  useEffect(() => {
    if (!containerRef.current) return
    const el = containerRef.current
    const beatsPerMeasure = parseInt(timeSignature.split('/')[0], 10) || 4
    const beatValue = parseInt(timeSignature.split('/')[1], 10) || 4
    const voiceTimeSig = `${beatsPerMeasure}/${beatValue}`

    const sopMeas = groupIntoMeasures(score.soprano, beatsPerMeasure)
    const altMeas = groupIntoMeasures(score.alto, beatsPerMeasure)
    const tenMeas = groupIntoMeasures(score.tenor, beatsPerMeasure)
    const basMeas = groupIntoMeasures(score.bass, beatsPerMeasure)

    const numMeasures = Math.max(1, sopMeas.length, altMeas.length, tenMeas.length, basMeas.length)
    const effectiveWidth = Math.max(200, containerWidth > 0 ? containerWidth : 800)

    const hasChordAnalysis = Boolean(
      analysisResult?.measures?.some(
        (m) =>
          (m?.beats?.length && m.beats.some((b) => isValidChordSymbol(b?.chordSymbol))) ||
          isValidChordSymbol(m?.chordSymbol),
      ),
    )

    el.innerHTML = ''
    const renderer = new Renderer(el, Renderer.Backends.SVG)
    const ctx = renderer.getContext()
    ctx.setFont('Arial', 10, '')

    const measureWidths = []
    for (let i = 0; i < numMeasures; i++) {
      const aResult = getMeasureAnalysis(analysisResult, i)
      const voiceLists = [
        sopMeas[i]?.notes ?? [],
        altMeas[i]?.notes ?? [],
        tenMeas[i]?.notes ?? [],
        basMeas[i]?.notes ?? [],
      ]
      const noteWFirst = minStaveWidthForNoteContent(voiceLists, beatsPerMeasure, true)
      const noteWRest = minStaveWidthForNoteContent(voiceLists, beatsPerMeasure, false)
      let wFirst = Math.max(FIRST_W_BASE, noteWFirst)
      let wRest = Math.max(REST_W_BASE, noteWRest)
      if (aResult?.beats?.length) {
        wFirst = Math.max(wFirst, minStaveWidthForChordAnalysis(ctx, aResult.beats, beatsPerMeasure, true))
        wRest = Math.max(wRest, minStaveWidthForChordAnalysis(ctx, aResult.beats, beatsPerMeasure, false))
      }
      measureWidths.push({ wFirst, wRest })
    }

    // 한 줄로 펼쳐서 오른쪽으로 스크롤해서 보는 방식 — 줄바꿈 없이 모든 마디를 row 0에
    // 이어붙인다(.score-wrap의 overflow-x:auto가 실제 스크롤을 담당).
    const layouts = []
    let mi = 0
    const row = 0
    let x = X0
    while (mi < numMeasures) {
      const firstInRow = mi === 0
      const mw = firstInRow ? measureWidths[mi].wFirst : measureWidths[mi].wRest
      layouts.push({ mi, row, x, w: mw, isFirstCol: firstInRow })
      x += mw
      mi++
    }
    const totalContentWidth = x + 4

    const numRows = 1
    // 한 줄 레이아웃에서는 ROW_H가 더 이상 "다음 줄과의 간격"을 책임지지 않으므로
    // 화음 기호 아래 여백을 줄인다(원래 332 → 316 → 305, 누적 약 50%↓).
    const ROW_H = hasChordAnalysis ? 305 : 310
    const EXTRA_H = hasChordAnalysis ? 16 : 0
    const SVG_H = numRows * ROW_H + EXTRA_H + 20
    const SVG_W = Math.max(effectiveWidth, totalContentWidth)
    renderer.resize(SVG_W, SVG_H)

    const VEX_KEYS = {
      C: 'C',
      G: 'G',
      D: 'D',
      A: 'A',
      E: 'E',
      B: 'B',
      'F#': 'F#',
      F: 'F',
      Bb: 'Bb',
      Eb: 'Eb',
      Ab: 'Ab',
      Db: 'Db',
      Gb: 'Gb',
    }

    const newHitMap = []
    const newMeasureBounds = []
    const measureAnnot = []
    /** 공통화음 전조 이음줄('^' 접두 표기)용 — 그려진 순서대로 화음 기호 위치 기록 */
    const chordLabelPoints = []
    let cumFlatS = 0
    let cumFlatA = 0
    let cumFlatT = 0
    let cumFlatB = 0
    let cumNonS = 0
    let cumNonA = 0
    let cumNonT = 0
    let cumNonB = 0

    for (const lay of layouts) {
      const i = lay.mi
      const rowY = lay.row * ROW_H
      const x = lay.x
      const w = lay.w
      const isFirst = lay.isFirstCol
      const isFinal = i === numMeasures - 1

      // 마디 첫 박에 임시표가 있으면(특히 두 성부 이상 겹치면) VexFlow의 스태프 자체
      // noteStartX(음표 시작 x)가 앞 마디 쪽에 너무 가까워서 임시표가 마디선 밖으로
      // 삐져나온다 — Stave 폭(w)만 늘려서는 noteStartX 자체가 안 밀리므로(justifyWidth만
      // 넓어짐) 소용없었다(사용자 확인). noteStartX를 직접 오른쪽으로 밀어야 한다.
      // 트레블·베이스 두 스태프에 서로 다른 보너스를 주면(각자 자기 성부 임시표
      // 개수만 보고) noteStartX가 서로 어긋나서 같은 박인데 위아래 x가 안 맞게 된다
      // (사용자 확인) — 4성부 전체 중 최대 임시표 개수 하나로 통일해서 트레블·베이스
      // 둘 다 똑같이 밀어야 상하 정렬이 유지된다.
      const accCountFirstBeat = (notes) => {
        const first = (notes ?? []).find((n) => !n?.isRest)
        return first?.pitch && /[#bn]/.test(first.pitch) ? 1 : 0
      }
      const trebleAccCount = accCountFirstBeat(sopMeas[i]?.notes) + accCountFirstBeat(altMeas[i]?.notes)
      const bassAccCount = accCountFirstBeat(tenMeas[i]?.notes) + accCountFirstBeat(basMeas[i]?.notes)
      const sharedAccCount = Math.max(trebleAccCount, bassAccCount)
      const accStartXBonus = sharedAccCount <= 0 ? 0 : sharedAccCount === 1 ? 16 : 16 + (sharedAccCount - 1) * 20

      const ts = new Stave(x, rowY + TREBLE_Y, w)
      if (isFirst) {
        ts.addClef('treble')
        if (i === 0) {
          ts.addKeySignature(VEX_KEYS[keySignature] ?? 'C')
          ts.addTimeSignature(timeSignature)
        }
      }
      if (isFinal) ts.setEndBarType(Barline.type.END)
      if (accStartXBonus > 0) ts.setNoteStartX(ts.getNoteStartX() + accStartXBonus)
      ts.setContext(ctx).draw()

      const bs = new Stave(x, rowY + BASS_Y, w)
      if (isFirst) {
        bs.addClef('bass')
        if (i === 0) {
          bs.addKeySignature(VEX_KEYS[keySignature] ?? 'C')
          bs.addTimeSignature(timeSignature)
        }
      }
      if (isFinal) bs.setEndBarType(Barline.type.END)
      if (accStartXBonus > 0) bs.setNoteStartX(bs.getNoteStartX() + accStartXBonus)
      bs.setContext(ctx).draw()

      if (isFirst) {
        new StaveConnector(ts, bs).setType(3).setContext(ctx).draw()
        new StaveConnector(ts, bs).setType(1).setContext(ctx).draw()
      }
      new StaveConnector(ts, bs).setType(isFinal ? 6 : 0).setContext(ctx).draw()

      newMeasureBounds.push({
        measureIndex: i,
        rowY,
        x1: x,
        y1: rowY + TREBLE_Y - 8,
        x2: x + w,
        y2: rowY + BASS_Y + 120,
        splitY: rowY + GRAND_STAFF_SPLIT_Y,
      })

      // 마디 번호
      ctx.save()
      ctx.setFont('Arial', 9, 'normal')
      ctx.setFillStyle('#adb5bd')
      ctx.fillText(String(i + 1), x + (isFirst ? 2 : 3), rowY + TREBLE_Y - 10)
      ctx.restore()

      const sopNotes = sopMeas[i]?.notes ?? []
      const altNotes = altMeas[i]?.notes ?? []
      const tenNotes = tenMeas[i]?.notes ?? []
      const basNotes = basMeas[i]?.notes ?? []

      const sopTrunc = truncateNotesForMeasure(sopNotes, beatsPerMeasure)
      const altTrunc = truncateNotesForMeasure(altNotes, beatsPerMeasure)
      const tenTrunc = truncateNotesForMeasure(tenNotes, beatsPerMeasure)
      const basTrunc = truncateNotesForMeasure(basNotes, beatsPerMeasure)

      const sv = fillMeasureToBeats(sopNotes, beatsPerMeasure, 'treble', 1, 'soprano', theoryBase)
      const av = fillMeasureToBeats(altNotes, beatsPerMeasure, 'treble', -1, 'alto', theoryBase)
      const tv = fillMeasureToBeats(tenNotes, beatsPerMeasure, 'bass', 1, 'tenor', theoryBase)
      const bv = fillMeasureToBeats(basNotes, beatsPerMeasure, 'bass', -1, 'bass', theoryBase)

      // 유니즌(완전히 같은 음)을 공유하는 두 성부는 임시표도 하나만 — 폭 계산(포매팅) 전에
      // 미리 지워야 그 폭이 마디 너비에 잡히지 않는다.
      dedupeUnisonAccidentals(sv, sopTrunc, av, altTrunc)
      dedupeUnisonAccidentals(tv, tenTrunc, bv, basTrunc)

      const svVoice = new Voice(voiceTimeSig)
      const avVoice = new Voice(voiceTimeSig)
      const tvVoice = new Voice(voiceTimeSig)
      const bvVoice = new Voice(voiceTimeSig)
      for (const v of [svVoice, avVoice, tvVoice, bvVoice]) v.setStrict(true)

      svVoice.addTickables(sv)
      avVoice.addTickables(av)
      tvVoice.addTickables(tv)
      bvVoice.addTickables(bv)

      // 트레블(소프라노·알토)과 베이스(테너·베이스)를 따로 formatToStave하면 각자
      // 독립적으로 박자 폭을 배분하기 때문에, 한쪽 성부에만 임시표가 붙는 등
      // 폭 요구량이 달라지면 같은 박이라도 그랜드 스태프 상하 x가 어긋난다.
      // 4성부를 하나의 Formatter로 함께 포매팅해 같은 박의 x를 동기화한다.
      const allVoices = [svVoice, avVoice, tvVoice, bvVoice]
      const justifyWidth = ts.getNoteEndX() - ts.getNoteStartX() - Stave.defaultPadding
      const runCombinedFormat = () => {
        const formatter = new Formatter()
        formatter.joinVoices([svVoice, avVoice])
        formatter.joinVoices([tvVoice, bvVoice])
        formatter.format(allVoices, justifyWidth, { align_rests: false })
        formatter.postFormat()
      }
      try {
        runCombinedFormat()
      } catch (e) {
        console.warn('Formatter error measure', i, e)
        for (const v of allVoices) v.setStrict(false)
        runCombinedFormat()
      }

      // 동일 길이 쉼표 병합으로 숨겨진 A/B 등을 다시 보이게 + 세로 위치 고정
      ensureSatbRestsVisible(sv, av, tv, bv)

      // 화음(박)이 시작하는 지점의 tick context X를 박자 비율로 재배치해 임시표
      // 유무와 무관하게 균등한 간격을 만든다. VexFlow의 Note.getAbsoluteX()는
      // tickContext.getX() + stave.getNoteStartX()를 더해서 최종 좌표를 만들므로
      // (이전 시도들이 여기서 stave 시작좌표를 중복으로 더해 다음 마디를 침범하는
      // 버그가 있었다) tick context에는 stave 기준 "상대" 값만 넣는다.
      try {
        if (Number.isFinite(justifyWidth) && justifyWidth > 0) {
          const seenCtx = new Set()
          const applyToVoice = (vex, trunc) => {
            let cursor = 0
            for (let j = 0; j < trunc.length && j < vex.length; j++) {
              const n = trunc[j]
              const beats = BEAT_VALUES[n.duration] ?? 1
              const startBeat = cursor
              cursor += beats
              if (n.isRest) continue
              const tc = vex[j]?.getTickContext?.()
              if (!tc || seenCtx.has(tc)) continue
              const frac = Math.min(1, startBeat / beatsPerMeasure)
              const x = frac * justifyWidth
              if (!Number.isFinite(x)) continue
              tc.setX(x)
              seenCtx.add(tc)
            }
          }
          applyToVoice(sv, sopTrunc)
          applyToVoice(av, altTrunc)
          applyToVoice(tv, tenTrunc)
          applyToVoice(bv, basTrunc)
        }
      } catch (e) {
        console.warn('Chord spacing reposition failed for measure', i, e)
      }

      // VexFlow의 2성부 충돌 회피 포매터가 (온음표처럼 대가 없는 반대 성부 음표와
      // 부딪힐 때) 성부 규칙과 무관하게 대 방향을 뒤집는 경우가 있음 — 소프라노 꼬리가
      // 알토 쪽으로 뒤집혀 알토 음표를 가리는 원인. 포매팅 직후 성부별 규칙(소프라노·
      // 테너 위, 알토·베이스 아래)을 다시 강제해 되돌린다.
      const forceStemDir = (arr, dir) => {
        for (const n of arr) {
          if (n?.hasStem?.() && n.getStemDirection?.() !== dir) n.setStemDirection(dir)
        }
      }
      forceStemDir(sv, 1)
      forceStemDir(av, -1)
      forceStemDir(tv, 1)
      forceStemDir(bv, -1)

      // 소프라노/테너 꼬리(스템·빔)가 바로 아래 알토/베이스 음표 머리를 가리지 않도록
      // 음높이가 가까운(4도 미만) 구간에서 위 성부 음표를 오른쪽으로 살짝 이동
      avoidStemHeadCollision(sv, sopTrunc, altTrunc)
      avoidStemHeadCollision(tv, tenTrunc, basTrunc)

      // 8분·16분 연속음 빔 — 개별 깃발보다 폭이 줄어 마디 넘침도 완화
      const measureBeams = []
      for (const arr of [sv, av, tv, bv]) {
        try {
          measureBeams.push(...Beam.generateBeams(arr, { maintain_stem_directions: true }))
        } catch { /* ignore */ }
      }

      const aResult = getMeasureAnalysis(analysisResult, i)
      const parallelSpans = collectParallelIssueSpansForMeasure(analysisResult, i, beatsPerMeasure)
      applyParallelIssueStylesToVoice(sv, sopTrunc, 'soprano', parallelSpans, beatsPerMeasure)
      applyParallelIssueStylesToVoice(av, altTrunc, 'alto', parallelSpans, beatsPerMeasure)
      applyParallelIssueStylesToVoice(tv, tenTrunc, 'tenor', parallelSpans, beatsPerMeasure)
      applyParallelIssueStylesToVoice(bv, basTrunc, 'bass', parallelSpans, beatsPerMeasure)

      applySelectionStyle(sv, sopTrunc, cumFlatS, selectedNote, 'soprano')
      applySelectionStyle(av, altTrunc, cumFlatA, selectedNote, 'alto')
      applySelectionStyle(tv, tenTrunc, cumFlatT, selectedNote, 'tenor')
      applySelectionStyle(bv, basTrunc, cumFlatB, selectedNote, 'bass')

      // draw 직전 한 번 더 — ModifierContext가 숨긴 쉼표 복구
      ensureSatbRestsVisible(sv, av, tv, bv)

      svVoice.draw(ctx, ts)
      avVoice.draw(ctx, ts)
      tvVoice.draw(ctx, bs)
      bvVoice.draw(ctx, bs)
      for (const beam of measureBeams) {
        try { beam.setContext(ctx).draw() } catch { /* ignore */ }
      }

      measureAnnot.push({
        measureIndex: i,
        row: lay.row,
        rowY,
        heads: {
          soprano: buildVoiceHeadSpans(sv, sopTrunc, beatsPerMeasure),
          alto: buildVoiceHeadSpans(av, altTrunc, beatsPerMeasure),
          tenor: buildVoiceHeadSpans(tv, tenTrunc, beatsPerMeasure),
          bass: buildVoiceHeadSpans(bv, basTrunc, beatsPerMeasure),
        },
        transitions: collectParallelTransitions(aResult, beatsPerMeasure),
        lastBeatSpan: (() => {
          const blocks = beatBlocksToSpanBlocks(aResult?.beats, beatsPerMeasure)
          return blocks.length ? { start: blocks[blocks.length - 1].start, end: blocks[blocks.length - 1].end } : null
        })(),
      })

      // NCT 라벨 (비화성음 표시)
      if (aResult?.beats?.some((b) => b.nct)) {
        const NCT_VOICES = [
          { voice: 'soprano', notes: sopTrunc, vexArr: sv },
          { voice: 'alto',    notes: altTrunc, vexArr: av },
          { voice: 'tenor',   notes: tenTrunc, vexArr: tv },
          { voice: 'bass',    notes: basTrunc, vexArr: bv },
        ]
        ctx.save()
        ctx.setFont('Arial', 8, 'bold')
        ctx.setFillStyle('#7c3aed')
        for (const { voice, notes, vexArr } of NCT_VOICES) {
          let beatAcc = 0
          for (let j = 0; j < notes.length; j++) {
            const dur = notes[j].duration?.replace('r', '') ?? 'q'
            const durBeats = BEAT_VALUES[dur] ?? 1
            if (!notes[j].isRest) {
              // nctStart: 해당 성부 NCT 음의 마디 내 시작 박(0-index) — 백엔드/로컬 공통
              const block = aResult.beats.find((b) =>
                b.nctStart?.[voice] != null
                  ? Math.abs(b.nctStart[voice] - beatAcc) < 0.001
                  : b.nct?.[voice] != null && Math.abs(b.beat - beatAcc) < 0.001,
              )
              const nctType = block?.nct?.[voice]
              if (nctType) {
                try {
                  const vn = vexArr[j]
                  const nx = vn?.getAbsoluteX()
                  const ny = vn?.getYs()?.[0]
                  if (nx != null && ny != null) {
                    // 대(stem)가 위로 향하면 음표 머리 바로 위는 대·빔과 겹치므로
                    // 대 끝(topY) 위로 라벨을 올려 피한다. 대가 아래로 향하면
                    // 머리 위쪽이 비어 있으므로 그대로 둔다.
                    let ly = ny - 10
                    if (vn?.hasStem?.() && vn.getStemDirection?.() === 1) {
                      const ext = vn.getStemExtents?.()
                      if (Number.isFinite(ext?.topY)) ly = ext.topY - 8
                    }
                    ctx.fillText(nctType, nx - 5, ly)
                  }
                } catch { /* ignore */ }
              }
            }
            beatAcc += durBeats
          }
        }
        ctx.restore()
      }

      if (
        selectedMeasure != null
        && selectedMeasure.measureIndex === i
        && (selectedMeasure.staff === 'upper' || selectedMeasure.staff === 'lower')
      ) {
        const split = rowY + GRAND_STAFF_SPLIT_Y
        const top = rowY + TREBLE_Y - 8
        const bot = rowY + BASS_Y + 120
        ctx.save()
        ctx.setFillStyle('rgba(29, 78, 216, 0.14)')
        if (selectedMeasure.staff === 'upper') {
          ctx.fillRect(x, top, w, Math.max(4, split - top))
        } else {
          ctx.fillRect(x, split, w, Math.max(4, bot - split))
        }
        ctx.restore()
      }

      const rs = collectHitmap(sv, withFillerEntries(sopTrunc, beatsPerMeasure), cumNonS, cumFlatS, 'soprano', newHitMap)
      cumNonS += rs.nonRestCount
      cumFlatS += sopTrunc.length
      const ra = collectHitmap(av, withFillerEntries(altTrunc, beatsPerMeasure), cumNonA, cumFlatA, 'alto', newHitMap)
      cumNonA += ra.nonRestCount
      cumFlatA += altTrunc.length
      const rt = collectHitmap(tv, withFillerEntries(tenTrunc, beatsPerMeasure), cumNonT, cumFlatT, 'tenor', newHitMap)
      cumNonT += rt.nonRestCount
      cumFlatT += tenTrunc.length
      const rb = collectHitmap(bv, withFillerEntries(basTrunc, beatsPerMeasure), cumNonB, cumFlatB, 'bass', newHitMap)
      cumNonB += rb.nonRestCount
      cumFlatB += basTrunc.length

      const inner = chordLabelInnerMetrics(x, w, isFirst)

      if (aResult?.beats?.length) {
        const beatNums = aResult.beats.map((b) => Number(b?.beat)).filter((v) => Number.isFinite(v))
        const beatBaseOffset = beatNums.length > 0 && Math.min(...beatNums) >= 1 ? 1 : 0
        const mapS = buildBeatCenterXMap(sv, beatsPerMeasure)
        const mapA = buildBeatCenterXMap(av, beatsPerMeasure)
        const mapT = buildBeatCenterXMap(tv, beatsPerMeasure)
        const mapB = buildBeatCenterXMap(bv, beatsPerMeasure)

        const layoutItems = []
        let prevChordSymbol = null
        for (const ab of aResult.beats) {
          if (!isValidChordSymbol(ab.chordSymbol)) continue
          // 연속된 동일 기호는 한 번만 표시 (비화성음으로 인한 중복 방지)
          if (ab.chordSymbol === prevChordSymbol) continue
          prevChordSymbol = ab.chordSymbol
          const bIdx = beatIndexForHarmonyBlock(ab, beatBaseOffset, beatsPerMeasure)
          const anchorX = chordBeatAnchorXFromMaps(bIdx, mapS, mapA, mapT, mapB)
          if (anchorX == null) continue
          const slotMaxW = Math.max(48, inner.innerW / Math.max(1, beatsPerMeasure) - 8)
          const hw = harmonyHalfExtentForLayout(ctx, ab.chordSymbol, slotMaxW)
          layoutItems.push({ beat: bIdx, anchorX, cx: anchorX, hw, chordSymbol: ab.chordSymbol, maxW: slotMaxW })
        }
        if (layoutItems.length > 0) {
          const { boundL, boundR } = harmonyLayoutHorizontalBounds(layoutItems, x, w, inner.minX, inner.maxX, 6)
          placeHarmonyLabelsAnchored(layoutItems, boundL, boundR, 8)
          recomputeHarmonyMaxWidthsAfterSeparate(layoutItems, inner.minX, inner.maxX, 10)
          for (const it of layoutItems) {
            const edge = drawHarmonyChordAtCx(ctx, it.cx, rowY, it.chordSymbol, it.maxW)
            if (edge) chordLabelPoints.push({ ...edge, rowY, raw: it.chordSymbol })
          }
        }
      } else if (isValidChordSymbol(aResult?.chordSymbol)) {
        const cx = inner.minX + inner.innerW / 2
        const slotMaxW = Math.max(48, inner.innerW - 16)
        const edge = drawHarmonyChordAtCx(ctx, cx, rowY, aResult.chordSymbol, slotMaxW)
        if (edge) chordLabelPoints.push({ ...edge, rowY, raw: aResult.chordSymbol })
      }

      // 비화성음(경과음 등) — 화음 기호 자리가 아니라 그 음표 머리 위/아래에 "×"로 표시.
      // 대(stem)가 위로 향하면 머리 아래, 대가 아래로 향하면 머리 위(대와 겹치지 않는 쪽).
      const nctMarkVoices = [
        { notes: sopTrunc, vexArr: sv },
        { notes: altTrunc, vexArr: av },
        { notes: tenTrunc, vexArr: tv },
        { notes: basTrunc, vexArr: bv },
      ]
      for (const { notes, vexArr } of nctMarkVoices) {
        for (let j = 0; j < notes.length; j++) {
          if (!notes[j]?.isNCT) continue
          const vn = vexArr[j]
          if (!vn) continue
          const cx = staveNoteHeadCenterX(vn)
          const ny = vn.getYs?.()?.[0]
          if (cx == null || ny == null) continue
          const stemUp = vn.hasStem?.() ? vn.getStemDirection?.() === 1 : true
          const my = stemUp ? ny + 16 : ny - 10
          ctx.save()
          ctx.setFont('Georgia', 14, 'bold')
          ctx.setFillStyle('#1e3a8a')
          ctx.fillText('×', cx - 4, my)
          ctx.restore()
        }
      }

      if (playbackCursor && playbackCursor.measureIndex === i) {
        const b = playbackCursor.beatInMeasure
        const bpp = beatsPerMeasure
        const padL = isFirst ? 52 : 18
        const padR = 14
        const innerW = Math.max(8, w - padL - padR)
        const frac = Math.min(1, Math.max(0, b / bpp))
        const cx = x + padL + frac * innerW
        ctx.save()
        ctx.setStrokeStyle('#e11d48')
        ctx.setLineWidth(2)
        ctx.beginPath()
        ctx.moveTo(cx, rowY + TREBLE_Y - 10)
        ctx.lineTo(cx, rowY + BASS_Y + 108)
        ctx.stroke()
        ctx.restore()
      }
    }

    // 줄(rowY)별 오선 실제 좌우 끝 — 전조 붙임줄이 줄바뀜 시 캔버스 끝까지 안 뻗고
    // 그 줄 오선 끝에서 멈추도록.
    const rowEdgesByY = new Map()
    for (const mb of newMeasureBounds) {
      const e = rowEdgesByY.get(mb.rowY) ?? { minX: mb.x1, maxX: mb.x2 }
      e.minX = Math.min(e.minX, mb.x1)
      e.maxX = Math.max(e.maxX, mb.x2)
      rowEdgesByY.set(mb.rowY, e)
    }

    // 공통화음 전조 이음줄 — chordSymbol을 '^'로 시작하게 표기하면, 바로 앞에
    // 그려진 화음 기호(보통 공통화음 피벗)에서 이 화음으로 붙임줄 모양 곡선을 그린다.
    // 피벗과 확정 화음이 줄(시스템)이 바뀌어 떨어져 있으면, 양쪽 줄 가장자리에 화살표로 표시.
    for (let k = 1; k < chordLabelPoints.length; k++) {
      const cur = chordLabelPoints[k]
      if (typeof cur.raw !== 'string' || !cur.raw.startsWith('^')) continue
      const prev = chordLabelPoints[k - 1]
      if (prev && prev.rowY === cur.rowY) {
        drawModulationTie(ctx, prev, cur)
      } else if (prev) {
        const prevRowEdges = rowEdgesByY.get(prev.rowY)
        drawModulationTieRowEdges(ctx, prev, cur, prevRowEdges ? prevRowEdges.maxX : effectiveWidth - 4)
      }
    }

    // 병행 시각화 — 음표·로마숫자 그린 뒤, 성부 쌍 사선 + 짧은 라벨
    for (let mi = 0; mi < measureAnnot.length; mi++) {
      const md = measureAnnot[mi]
      // 같은 박 전이(beat span)에 두 개 이상의 병행(예: S-B 8도 + A-T 5도)이 동시에
      // 잡히면 라벨이 같은 자리에 겹쳐 찍힌다 — 전이별로 순서를 세어 세로로 쌓는다.
      const labelStackByTransition = new Map()
      for (const t of md.transitions) {
        if (!t.prev) continue
        const prevH = resolveParallelHeads(md.heads, t.voices, t.prev)
        const currH = resolveParallelHeads(md.heads, t.voices, t.curr)
        if (!prevH || !currH) continue
        const stackKey = `${t.prev.start}-${t.prev.end}|${t.curr.start}-${t.curr.end}`
        const stackIndex = labelStackByTransition.get(stackKey) ?? 0
        labelStackByTransition.set(stackKey, stackIndex + 1)
        drawParallelMotionMark(ctx, {
          p1: prevH.p1,
          p2: prevH.p2,
          c1: currH.p1,
          c2: currH.p2,
          label: t.label,
          rowY: md.rowY,
          labelStackIndex: stackIndex,
          kind: t.kind,
        })
      }

      // 마디 경계 병행: 다음 마디 첫 박 이슈 → 이 마디 마지막 ↔ 다음 마디 첫 박
      const next = measureAnnot[mi + 1]
      if (!next) continue
      const cross = next.transitions.filter((t) => t.prev == null)
      if (cross.length === 0 || !md.lastBeatSpan) continue

      const nextFirstSpan = (() => {
        const blocks = beatBlocksToSpanBlocks(
          getMeasureAnalysis(analysisResult, next.measureIndex)?.beats,
          beatsPerMeasure,
        )
        return blocks.length ? { start: blocks[0].start, end: blocks[0].end } : { start: 0, end: 1 }
      })()

      for (const t of cross) {
        const prevH = resolveParallelHeads(md.heads, t.voices, md.lastBeatSpan)
        const currH = resolveParallelHeads(next.heads, t.voices, nextFirstSpan)
        if (prevH && currH && md.row === next.row) {
          drawParallelMotionMark(ctx, {
            p1: prevH.p1,
            p2: prevH.p2,
            c1: currH.p1,
            c2: currH.p2,
            label: t.label,
            rowY: md.rowY,
            kind: t.kind,
          })
          continue
        }
        // 줄(Staff)이 바뀌어 하나의 사선으로 잇지 못하는 경우 — 양쪽 줄에
        // 각각 방향 표시용 스텁 사선 + 라벨을 남겨 병행이 줄을 건너
        // 이어짐을 표시한다.
        if (prevH) {
          drawParallelRowEdgeMark(ctx, {
            h1: prevH.p1,
            h2: prevH.p2,
            dir: 1,
            label: t.label,
            rowY: md.rowY,
          })
        }
        if (currH) {
          drawParallelRowEdgeMark(ctx, {
            h1: currH.p1,
            h2: currH.p2,
            dir: -1,
            label: t.label,
            rowY: next.rowY,
          })
        }
      }
    }

    try {
      const groups = el.querySelectorAll('svg > g')
      // eslint-disable-next-line no-console
      console.log('[kordAI-rest-debug3]', {
        totalTopGroups: groups.length,
        svgChildCount: el.querySelector('svg')?.childElementCount,
        outerHTMLSample: el.querySelector('svg')?.outerHTML?.slice(0, 4000),
      })
    } catch { /* ignore */ }

    noteHitMapRef.current = newHitMap
    measureBoundsRef.current = newMeasureBounds
  }, [score, keySignature, timeSignature, analysisResult, containerWidth, playbackCursor, selectedNote, selectedMeasure, theoryBase])

  return (
    <div className="score-wrap" ref={wrapRef}>
      <div
        ref={containerRef}
        className="score-container"
        style={{
          cursor: 'default',
          userSelect: 'none',
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
        }}
        onMouseDown={handleMouseDown}
      />
    </div>
  )
}
